export interface FAQ {
  q: string;
  a: string;
}

export interface PostSeo {
  seoTitle: string; // 50–60 chars
  metaDescription: string; // 140–160 chars
  focusKeyword: string;
  secondaryKeywords: string[];
  lsiKeywords: string[];
  keyTakeaways: string[];
  faqs: FAQ[];
  updatedDate?: string;
  ogImage?: string; // absolute or root-relative
}

// Per-post SEO metadata. Keyed by post slug. Kept separate from the post
// module so we don't have to touch every content file to extend the schema.
export const POST_SEO: Record<string, PostSeo> = {
  "du-instagram-kurti-scam-cyber-fraud-2026": {
    seoTitle: "DU Student's ₹6,450 Kurti Scam Exposes ₹30-Crore Fraud Network",
    metaDescription:
      "A Ramjas College student's Instagram kurti order led Delhi Police to seven arrests and a ₹30-crore cyber fraud network. How the scam worked and how to protect yourself.",
    focusKeyword: "DU student Instagram scam",
    secondaryKeywords: ["Kavya kurti scam", "₹30 crore cyber fraud Delhi", "online shopping scam India"],
    lsiKeywords: ["1930 cybercrime helpline", "mule bank accounts", "NCRP complaint"],
    keyTakeaways: [
      "A second-year Ramjas College student lost ₹6,450 to a fake Instagram kurti shop.",
      "Her NCRP complaint led Delhi Police to seven arrests and three suspected shell firms.",
      "Police link 27 complaints from different states to the network, which routed over ₹30 crore.",
      "Call 1930 and file at cybercrime.gov.in immediately; never pay extra charges after an order.",
    ],
    faqs: [
      { q: "What should I do first if an online seller scams me?", a: "Call the cybercrime helpline 1930 right away, then file a complaint at cybercrime.gov.in with screenshots of the chat and payments." },
      { q: "Is a small scam like ₹800 worth reporting?", a: "Yes. Small complaints share the same mule accounts, and in this case one student's report helped expose a network allegedly worth over ₹30 crore." },
      { q: "Can I get a refund after paying a fake online seller?", a: "Fast reporting to 1930 sometimes freezes the money before it moves on, but there is no guarantee. Never pay extra charges to unlock a refund." },
    ],
  },
  "du-hindi-phd-interview-protest-2026": {
    seoTitle: "DU Hindi PhD Interviews Stalled After Overnight Notice: Full Story",
    metaDescription:
      "DU's Hindi Department couldn't hold PhD interviews on 28 October after ABVP and DUSU protested a notice issued the night before. What happened and what applicants should do.",
    focusKeyword: "DU Hindi PhD admission protest",
    secondaryKeywords: ["DU PhD interview short notice", "DU Hindi Department PhD seats", "NET JRF PhD admission DU"],
    lsiKeywords: ["UGC PhD regulations", "DUSU Yash Dabas", "ABVP protest DU"],
    keyTakeaways: [
      "DU's Hindi Department issued its PhD interview notice on the evening of 27 October for a 9:15 am report time.",
      "Interviews on 28 October could not be held after ABVP and DUSU protested the short notice and shortlist.",
      "Protesters say eligible candidates, including NET-qualified ones, missed out; the department has 150+ PhD seats.",
      "Applicants should check department pages daily and keep written records of eligibility and complaints.",
    ],
    faqs: [
      { q: "Why were DU's Hindi Department PhD interviews cancelled on 28 October?", a: "ABVP and DUSU protested the overnight notice and the alleged exclusion of eligible candidates, and the interviews could not be conducted." },
      { q: "Are NET-qualified candidates eligible for DU PhD admissions?", a: "Yes, under UGC guidelines NET-qualified and Only for PhD candidates are eligible alongside JRF qualifiers, which was the basis of an earlier April 2026 protest in the same department." },
      { q: "What should shortlisted PhD candidates do now?", a: "Watch the department's notifications for a revised schedule, keep documents and presentation ready, and keep written records of all communication." },
    ],
  },
  "du-5889-crore-hostel-plan-what-it-means-2026": {
    seoTitle: "DU ₹5,889 Crore Hostel Plan 2026: 21,895 Beds, Locations, Timeline",
    metaDescription:
      "Delhi University approved hostels for 21,895 students and may convert empty staff quarters in North Campus. Where they will be and what PG students should do now.",
    focusKeyword: "DU hostel plan 2026",
    secondaryKeywords: ["Delhi University new hostels", "DU 5889 crore hostel", "DU staff quarters hostel"],
    lsiKeywords: ["Dhaka Complex hostel", "Jubilee Hall reconstruction", "North Campus PG rent"],
    keyTakeaways: [
      "DU's Executive Council approved ₹5,889 crore for 21,895 hostel beds on 13 October 2026.",
      "Most new beds are at Dhaka Complex, Najafgarh, Dwarka and Surajmal Vihar.",
      "Turning vacant Type-I staff quarters into rooms is only a recommendation so far.",
      "No completion date has been announced, so plan for at least another year of PG living.",
    ],
    faqs: [
      { q: "How many students will DU's new hostels house?", a: "21,895: 15,758 in new hostels and 6,137 in rebuilt Jubilee Hall and the PG Men's Hostel." },
      { q: "When will the new DU hostels open?", a: "DU has not announced a completion date. Earlier hostel projects have been delayed." },
      { q: "Are the new hostels in North Campus?", a: "Some are, at Dhaka Complex. Others are at Najafgarh, Dwarka and Surajmal Vihar." },
    ],
  },
  "du-one-year-pg-pse-priority-high-court-2026": {
    seoTitle: "DU One-Year PG Case in High Court: P1 to P4 Priority Explained",
    metaDescription:
      "About 100 students challenged Delhi University's one-year master's admission rules in the High Court. The P1 to P4 priority system explained, and what it means for you.",
    focusKeyword: "DU one year PG high court",
    secondaryKeywords: ["DU PSE priority P1 P2", "DU one year masters admission 2026", "four year UG one year PG"],
    lsiKeywords: ["UGCF 2022", "programme-specific eligibility", "DU direct PhD entrance test"],
    keyTakeaways: [
      "Around 100 petitioners have challenged DU's one-year PG admission rules in the Delhi High Court.",
      "The PSE system ranks applicants P1 to P4, with directly relevant degrees first.",
      "Students say general and programme graduates are disadvantaged and seats go unfilled.",
      "Plan your fourth year with PG priority rules in mind, and keep the two-year route open.",
    ],
    faqs: [
      { q: "What is P1 in DU's one-year PG admission?", a: "Candidates whose undergraduate degree is considered directly relevant to the master's programme. They get first preference." },
      { q: "Can BA Programme students apply for DU's one-year PG?", a: "Eligibility depends on the course, but under the current system they are usually in a lower priority level. This is being challenged in court." },
    ],
  },
  "active-recall-theory-subjects": {
    seoTitle: "Active Recall: How to Study Theory Subjects (2026)",
    metaDescription:
      "Highlighting isn't studying. A practical guide to active recall, spaced repetition and blank-page tests for theory-heavy Indian undergraduate papers.",
    updatedDate: "2026-07-28",
    focusKeyword: "active recall for theory subjects",
    secondaryKeywords: [
      "blank page test",
      "spaced repetition undergraduate",
      "how to study theory subjects",
      "study techniques Indian college",
    ],
    lsiKeywords: [
      "retrieval practice",
      "Anki",
      "cognitive psychology",
      "self-testing",
      "long-term retention",
    ],
    keyTakeaways: [
      "Recognition is not recall, rereading feels productive but rarely produces marks.",
      "The blank-page test, self-generated questions, and teaching aloud are the three highest-leverage recall formats.",
      "Spacing four one-hour sessions across a week beats one four-hour block on retention.",
      "For theory papers, pair recall with full-length timed answers; outlines alone hide the gap.",
    ],
    faqs: [
      {
        q: "Is active recall better than making notes?",
        a: "Note-making is useful only if the notes are then used for recall. Writing notes once and rereading them is close to passive review; the marks come from producing the material from memory, not from re-encountering it.",
      },
      {
        q: "How many times should I recall a topic before an exam?",
        a: "Four spaced retrievals, same day, three days later, one week later, three weeks later, is enough for most topics to reach durable long-term memory.",
      },
      {
        q: "Does active recall work for descriptive papers like sociology or political theory?",
        a: "Yes, but combine it with full timed answers. Recall consolidates the facts and definitions; timed answers train the argumentative style examiners actually mark.",
      },
      {
        q: "Do I need Anki or is paper fine?",
        a: "A running index card list with tick-marks for each recall works just as well. Anki automates the spacing but the underlying method is what matters.",
      },
    ],
  },
  "best-study-spots-north-campus": {
    seoTitle: "10 Best Study Spots in DU North Campus (2026)",
    metaDescription:
      "An honest map of study spots around DU North Campus, libraries, cafés and quiet corners that actually work when you need to get real work done.",
    focusKeyword: "best study spots DU north campus",
    secondaryKeywords: [
      "DU library timings",
      "Delhi University study cafes",
      "quiet places to study North Campus",
      "Ratan Tata library",
    ],
    lsiKeywords: [
      "Kamla Nehru Ridge",
      "Hudson Lane",
      "DSE library",
      "Patel Chest",
      "Vishwavidyalaya metro",
    ],
    keyTakeaways: [
      "Central libraries are best for deep work; cafés are better for reading and light revision.",
      "The most reliable spots are the ones with predictable Wi-Fi, seating and closing hours.",
      "Rotating between two or three venues prevents burnout and keeps focus fresh.",
    ],
    faqs: [
      {
        q: "Can non-DU students use DU libraries?",
        a: "Most college libraries are restricted to enrolled students, but the Ratan Tata Library and a few faculty libraries allow visiting readers with prior permission.",
      },
      {
        q: "Are cafés in Hudson Lane laptop-friendly?",
        a: "Most are, but expect one-purchase-per-two-hours etiquette during peak evenings. Weekday mornings are the quietest window.",
      },
      {
        q: "Which spot is best for group study?",
        a: "The lawns behind the Arts Faculty and the seminar rooms in Ramjas and Hindu (bookable) are the most reliable group-friendly options.",
      },
    ],
  },
  "cat-vs-civil-services-vs-masters": {
    seoTitle: "CAT vs UPSC vs Masters: Which to Pick After BA?",
    metaDescription:
      "An honest comparison of CAT, UPSC and a master's degree for Indian undergrads, timelines, cost, opportunity cost, and how to decide before third year.",
    focusKeyword: "CAT vs civil services vs masters",
    secondaryKeywords: [
      "MBA vs UPSC",
      "MA economics vs CAT",
      "career after economics honours",
      "MBA vs masters India",
    ],
    lsiKeywords: [
      "IIM",
      "UPSC prelims",
      "DSE",
      "GRE",
      "opportunity cost",
      "return on education",
    ],
    keyTakeaways: [
      "The three paths have very different risk profiles, MBA is high-cost, high-optionality; UPSC is high-variance, high-lock-in; a master's is low-cost, high-specialisation.",
      "Decide by the end of second year, third-year preparation without a decision is the most expensive mistake.",
      "The correct question is not 'which is best' but 'which fits my next five years'.",
    ],
    faqs: [
      {
        q: "Can I prepare for CAT and UPSC together?",
        a: "In practice, no. The two exams reward different skills and both are full-time projects at their serious stage. Preparing seriously for one and casually for the other almost always ends in a weak attempt at both.",
      },
      {
        q: "Is an MA from DSE better than an MBA from a Tier-2 IIM?",
        a: "It depends on your target career. For research, policy or PhD tracks, DSE dominates. For general management and higher starting pay, a Tier-2 IIM has broader placement optionality.",
      },
      {
        q: "When should I start UPSC prep?",
        a: "Serious preparation needs 12–18 months. Starting in the second half of third year is the earliest sensible point; earlier tends to hurt your undergraduate marks with limited benefit.",
      },
    ],
  },
  "du-economics-exam-first-5-minutes": {
    seoTitle: "DU Economics Exam Strategy: First 5 Minutes Guide",
    metaDescription:
      "How to read a DU economics question paper in the first five minutes, allocate time, spot traps and pick the right questions before you start writing.",
    focusKeyword: "DU economics exam strategy",
    secondaryKeywords: [
      "how to read a question paper",
      "DU exam time management",
      "economics honours exam tips",
      "question paper first five minutes",
    ],
    lsiKeywords: [
      "Section A",
      "internal choice",
      "marks allocation",
      "IS-LM graph",
      "multiplier derivation",
    ],
    keyTakeaways: [
      "The first five minutes are for reading the whole paper, not for starting to write.",
      "Budget roughly 2.5 minutes per mark, and reserve 15 minutes at the end for review.",
      "Start with the highest-marks question you are most confident on, not with question one.",
      "Wrap, don't finish, when you're 15 minutes over budget, a complete conclusion scores.",
    ],
    faqs: [
      {
        q: "Should I attempt questions in serial order?",
        a: "No. Serial order is a habit, not a rule. Marks per minute is highest on questions you know cold, so those should be attempted first regardless of number.",
      },
      {
        q: "How much time should I keep for review?",
        a: "Roughly 15 minutes on a 180-minute paper. Use the review window to finish the questions you wrapped short, not just to check calculations.",
      },
      {
        q: "What if I run out of time on the last question?",
        a: "Write a short skeleton, introduction, key argument, conclusion, instead of leaving it blank. Examiners award marks for structure and direction even without full elaboration.",
      },
    ],
  },
  "du-grading-attendance-explained": {
    seoTitle: "DU CGPA & 67% Attendance Rules Explained (2026)",
    metaDescription:
      "How DU's CGPA grading, internal assessment and 67% attendance rule actually work, with worked examples and the edge cases first-years never hear about.",
    focusKeyword: "DU grading and attendance rules",
    secondaryKeywords: [
      "DU CGPA calculation",
      "67 percent attendance rule",
      "DU internal assessment marks",
      "DU semester grading system",
    ],
    lsiKeywords: [
      "SGPA",
      "credit weight",
      "detained",
      "medical concession",
      "grace attendance",
    ],
    keyTakeaways: [
      "CGPA is a credit-weighted average, a four-credit paper counts more than a two-credit one.",
      "The 67% attendance rule is per course, not overall. Missing lectures in one paper can detain you even if your overall average is fine.",
      "Internal assessment is the cheapest 25 marks in the semester, don't skip assignments and class tests.",
    ],
    faqs: [
      {
        q: "Can I appear for exams with less than 67% attendance?",
        a: "Only with medical or authority-approved concession, and only up to the specified relaxation. Otherwise you're marked detained for that course and cannot sit the semester exam.",
      },
      {
        q: "How is SGPA converted to a final percentage?",
        a: "Multiply your SGPA by roughly 9.5 for an approximate percentage, DU publishes an exact conversion in the examination handbook.",
      },
      {
        q: "Do internal marks carry across semesters?",
        a: "No. Internal assessment is calculated fresh each semester per course and cannot be revised after the semester closes.",
      },
    ],
  },
  "elasticity-of-demand-indian-examples": {
    seoTitle: "Elasticity of Demand: Indian Examples & Formulas",
    metaDescription:
      "Price, income and cross elasticity of demand explained with everyday Indian examples, petrol, onions, Netflix and more. A no-jargon undergraduate guide.",
    focusKeyword: "elasticity of demand with Indian examples",
    secondaryKeywords: [
      "price elasticity examples India",
      "income elasticity demand",
      "cross elasticity substitutes",
      "microeconomics elasticity",
    ],
    lsiKeywords: [
      "necessity",
      "luxury",
      "substitutes",
      "complements",
      "law of demand",
      "revenue effect",
    ],
    keyTakeaways: [
      "Elasticity measures responsiveness, not slope, a flat curve is not automatically elastic.",
      "Petrol is inelastic in the short run and more elastic in the long run as consumers switch cars or commutes.",
      "Cross-elasticity sign tells you whether two goods are substitutes (positive) or complements (negative).",
    ],
    faqs: [
      {
        q: "Why is petrol demand called inelastic in India?",
        a: "Because most consumers can't quickly change vehicles or routes when prices rise. Over years, demand does adjust, so short-run elasticity is low, long-run elasticity is higher.",
      },
      {
        q: "Are onions elastic or inelastic?",
        a: "Onions are inelastic in the short run because they're a staple with limited substitutes in Indian cooking, which is why price spikes hit household budgets sharply.",
      },
      {
        q: "What's a good exam answer for income elasticity?",
        a: "Define it precisely, give one necessity (rice, near zero elasticity) and one luxury (foreign travel, elasticity above one) example, and mention Engel's law.",
      },
    ],
  },
  "fiscal-vs-monetary-policy": {
    seoTitle: "Fiscal vs Monetary Policy: Difference, RBI Examples",
    metaDescription:
      "Fiscal policy, monetary policy and how they interact, with RBI and Union Budget examples. A clear, exam-ready explanation for Indian undergraduates.",
    focusKeyword: "fiscal vs monetary policy",
    secondaryKeywords: [
      "RBI monetary policy",
      "Union Budget fiscal policy",
      "expansionary vs contractionary",
      "policy mix India",
    ],
    lsiKeywords: [
      "repo rate",
      "fiscal deficit",
      "aggregate demand",
      "inflation targeting",
      "crowding out",
    ],
    keyTakeaways: [
      "Fiscal policy is the government's tax and spending choices; monetary policy is the RBI's interest-rate and liquidity choices.",
      "Both target aggregate demand, but they act through different channels and at different speeds.",
      "In practice they must coordinate, a tight fiscal stance and loose monetary stance is a very different economy from the reverse.",
    ],
    faqs: [
      {
        q: "Which is more effective, fiscal or monetary policy?",
        a: "Neither is universally better. Monetary policy is faster and more flexible; fiscal policy has more direct effect when interest rates are low and demand is weak.",
      },
      {
        q: "What is the RBI's inflation target?",
        a: "4% CPI inflation with a tolerance band of 2% to 6%, set jointly with the Government of India and reviewed periodically.",
      },
      {
        q: "How does crowding out work?",
        a: "Higher government borrowing can raise interest rates, which discourages private investment, partially offsetting the fiscal expansion.",
      },
    ],
  },
  "gdp-deflator-vs-cpi": {
    seoTitle: "GDP Deflator vs CPI: Difference, Formula, Examples",
    metaDescription:
      "GDP deflator vs CPI explained with Indian data, coverage, weighting, and why they diverge. A concise undergraduate guide with examples.",
    focusKeyword: "GDP deflator vs CPI",
    secondaryKeywords: [
      "difference between deflator and CPI",
      "inflation measurement India",
      "WPI vs CPI vs deflator",
      "price index formula",
    ],
    lsiKeywords: [
      "Paasche index",
      "Laspeyres index",
      "base year",
      "nominal GDP",
      "real GDP",
    ],
    keyTakeaways: [
      "The CPI tracks a fixed consumer basket; the GDP deflator tracks whatever the economy actually produced this year.",
      "The CPI is a Laspeyres-type index; the deflator is closer to a Paasche index.",
      "The two can diverge sharply when import prices or a small set of sectors move, a normal, not pathological, feature.",
    ],
    faqs: [
      {
        q: "Which index is the RBI's inflation target based on?",
        a: "The RBI targets headline CPI inflation, not the GDP deflator or WPI.",
      },
      {
        q: "Why can WPI and CPI move in opposite directions?",
        a: "WPI is weighted heavily towards manufactured goods and fuels; CPI is dominated by food and services. Sector-specific shocks show up differently in each.",
      },
      {
        q: "Is the GDP deflator a good measure of consumer inflation?",
        a: "No. It reflects the price of everything produced, including exports and investment goods, not what households consume.",
      },
    ],
  },
  "honours-vs-programme": {
    seoTitle: "DU BA Honours vs Programme: Which Is Better in 2026?",
    metaDescription:
      "BA Honours vs BA Programme at Delhi University, course load, placements, further study and the real trade-offs first-year students should know.",
    focusKeyword: "BA honours vs BA programme",
    secondaryKeywords: [
      "DU honours vs programme",
      "difference honours programme DU",
      "which is better honours programme",
      "career after BA programme",
    ],
    lsiKeywords: [
      "single-honours",
      "multi-disciplinary",
      "CUET",
      "postgraduate eligibility",
      "specialisation",
    ],
    keyTakeaways: [
      "Honours is deeper and narrower; Programme is broader and lighter, and both are respected in the right contexts.",
      "For most competitive PG entries (DSE, IIMs), an Honours in the target subject is a genuine advantage.",
      "Programme students often have more time for exam prep, internships and secondary skills, which matters more than many admit.",
    ],
    faqs: [
      {
        q: "Is BA Programme respected for jobs?",
        a: "Yes, especially for civil services, banking and generalist roles where breadth matters. Sector-specific hiring often prefers Honours.",
      },
      {
        q: "Can I do an MA in Economics after BA Programme?",
        a: "Yes, subject to the university's eligibility rules. Some top programmes prefer BA (Hons) Economics, but many admit Programme students who clear the entrance test.",
      },
      {
        q: "Which is harder to score in?",
        a: "Honours has more specialisation and typically harder question papers; Programme has more courses but each is lighter. Averaged CGPAs are broadly comparable across cohorts.",
      },
    ],
  },
  "is-ma-economics-worth-it": {
    seoTitle: "Is MA Economics Worth It in 2026? Honest Review",
    metaDescription:
      "An honest look at the MA Economics degree in India, cost, career outcomes and who it actually helps. Written for undergraduates deciding by third year.",
    focusKeyword: "is MA economics worth it",
    secondaryKeywords: [
      "MA economics career",
      "DSE vs JNU vs IGIDR",
      "MA economics placement",
      "should I do MA in economics",
    ],
    lsiKeywords: [
      "econometrics",
      "PhD track",
      "policy research",
      "development economics",
      "quantitative finance",
    ],
    keyTakeaways: [
      "MA Economics is worth it if you need the quantitative depth for research, policy or a PhD, less so as a general prestige degree.",
      "DSE, ISI, IGIDR and JNU remain the strongest programmes for research-track careers.",
      "The opportunity cost is real, two years and forgone earnings, and should be weighed against MBA and direct-work paths.",
    ],
    faqs: [
      {
        q: "Do I need MA Economics for a PhD abroad?",
        a: "Not always, but strong quantitative preparation is expected. A rigorous Indian MA plus RA experience is one of the more reliable routes.",
      },
      {
        q: "Is MA Economics useful for finance jobs?",
        a: "For quantitative research or macro strategy, yes. For general banking and sales-trading, an MBA or CFA usually has better return on effort.",
      },
      {
        q: "How competitive is DSE admission?",
        a: "Extremely, a handful of hundreds of seats against thousands of applicants. Preparation of at least six months is standard.",
      },
    ],
  },
  "last-week-revision-macroeconomics": {
    seoTitle: "Last-Week Macroeconomics Revision Plan (7-Day Guide)",
    metaDescription:
      "A seven-day macroeconomics revision plan for the final week before your exam, priorities, past-paper drills and where cramming actually pays off.",
    focusKeyword: "last week macroeconomics revision",
    secondaryKeywords: [
      "macro revision plan",
      "exam week strategy",
      "IS-LM revision",
      "macroeconomics past papers",
    ],
    lsiKeywords: [
      "AD-AS",
      "Solow model",
      "multiplier",
      "monetary transmission",
      "past-paper drills",
    ],
    keyTakeaways: [
      "Prioritise the four to six models the paper reliably tests over reading fresh chapters.",
      "Draw every graph from memory, at least twice, in the final three days.",
      "Two full-length past papers under time pressure are worth more than any amount of passive reading.",
    ],
    faqs: [
      {
        q: "Should I attempt new topics in the last week?",
        a: "Only if you can master them in under two hours. Otherwise the time is better spent locking in topics you already half-know.",
      },
      {
        q: "How many past papers should I solve in the last week?",
        a: "Two full papers under strict timing, plus targeted questions from three or four more. Volume matters less than the honest self-marking.",
      },
      {
        q: "Is it worth pulling an all-nighter before the exam?",
        a: "No. Sleep loss reliably costs more marks than an extra revision session gains at this stage.",
      },
    ],
  },
  "study-timetable-you-actually-follow": {
    seoTitle: "How to Make a Study Timetable You'll Actually Follow",
    metaDescription:
      "Colour-coded timetables die on day three. Build a task-based, buffered plan you'll actually follow, a short, honest guide for Indian undergraduates.",
    focusKeyword: "study timetable college students",
    secondaryKeywords: [
      "how to make a study schedule",
      "study plan undergraduate",
      "weekly study plan",
      "task based timetable",
    ],
    lsiKeywords: [
      "focus blocks",
      "planning",
      "productivity",
      "buffer time",
      "Pomodoro",
    ],
    keyTakeaways: [
      "Uniform-energy timetables fail because real weeks have peaks, troughs and interruptions.",
      "Two focused 90-minute blocks per day is enough for a five-paper semester if used well.",
      "Plan by tasks, not by subjects, a task-based plan survives missed hours; a subject block does not.",
      "Leave 20% of your blocks empty as buffer for the disruptions you know are coming.",
    ],
    faqs: [
      {
        q: "How many hours a day should I study in college?",
        a: "Two to three hours of focused study on weekdays and a longer block on Sunday is enough for most Indian undergraduate courses if the time is genuinely focused.",
      },
      {
        q: "Should I use Pomodoro or 90-minute blocks?",
        a: "Both work. Pomodoro suits shorter tasks and lower focus days; 90-minute blocks suit deeper reading and problem-solving.",
      },
      {
        q: "What if I miss a whole day of my plan?",
        a: "Do not try to make up the hours the next day. Move the specific tasks back into the pool and let the next available block absorb them.",
      },
    ],
  },
  "useful-notes-at-revision-time": {
    seoTitle: "How to Make Study Notes That Work at Revision Time",
    metaDescription:
      "Most college notes are useless three months later. Here's how to make notes that pull their weight when the exam is a week away.",
    focusKeyword: "how to make useful study notes",
    secondaryKeywords: [
      "note taking college",
      "revision notes",
      "Cornell method",
      "notes for exams",
    ],
    lsiKeywords: [
      "Cornell",
      "outline method",
      "flashcards",
      "summary sheet",
      "condensed notes",
    ],
    keyTakeaways: [
      "Notes are for future-you, not present-you, write for the person revising three months from now.",
      "Condense once at the end of each chapter into a single summary page you'll actually reread.",
      "Verbatim lecture notes are worst; question-answer or Cornell layouts are consistently better.",
    ],
    faqs: [
      {
        q: "Should I make notes on a laptop or on paper?",
        a: "Paper for depth of processing, laptop for speed. Most students do best with paper for concept notes and a laptop for reference lists.",
      },
      {
        q: "Is the Cornell method worth the effort?",
        a: "Yes for lectures where you can reasonably keep up. The cue column is what makes the notes usable for recall later.",
      },
      {
        q: "How long should revision notes be?",
        a: "One to two A4 sides per chapter is the ceiling. If it's longer, it becomes another textbook you won't reread.",
      },
    ],
  },
  "what-economics-honours-grads-do": {
    seoTitle: "Careers After BA Economics Honours: Real Options 2026",
    metaDescription:
      "The honest career map for BA Economics Honours graduates in India, first jobs, master's paths, civil services and where the money and meaning actually go.",
    focusKeyword: "career after economics honours",
    secondaryKeywords: [
      "jobs after BA economics",
      "economics honours placement",
      "economics honours career options",
      "what economics grads do",
    ],
    lsiKeywords: [
      "research associate",
      "consulting",
      "policy",
      "civil services",
      "quant finance",
    ],
    keyTakeaways: [
      "Most Economics Honours graduates split between further study, RA/analyst roles and civil-services preparation.",
      "The starting salary distribution is wide, median jobs pay modestly; a small tail pays very well.",
      "The degree opens doors more than it pays a premium; specialisation after the degree does the paying.",
    ],
    faqs: [
      {
        q: "Do Economics Honours graduates get placed on campus?",
        a: "Yes at several top colleges, but placements are concentrated in a few sectors, consulting, analytics, banking, and are competitive.",
      },
      {
        q: "Is Economics Honours useful for civil services?",
        a: "Yes. It's a common optional and gives a strong grounding for General Studies economics topics.",
      },
      {
        q: "What percentage of Economics Honours students go for higher studies?",
        a: "At top DU colleges, roughly a third to a half continue directly into master's or MBA programmes within two years of graduation.",
      },
    ],
  },
  "where-students-lose-marks-numericals": {
    seoTitle: "Economics Numericals: 5 Ways Students Lose Marks",
    metaDescription:
      "The five specific places students lose marks on economics numericals, units, sign errors, missing graphs, and how to fix each before your next exam.",
    focusKeyword: "economics numericals common mistakes",
    secondaryKeywords: [
      "how to solve economics numericals",
      "marks in numericals",
      "economics exam mistakes",
      "unit errors economics",
    ],
    lsiKeywords: [
      "sign convention",
      "graph labels",
      "step marks",
      "rounding errors",
      "interpretation",
    ],
    keyTakeaways: [
      "Most numerical marks are lost in the last two lines, a right method, but a wrong unit or missing interpretation.",
      "Every step earns marks in Indian universities; skipping steps to save time is almost always negative EV.",
      "A one-line interpretation of the final number is the cheapest mark on the paper and the most commonly skipped.",
    ],
    faqs: [
      {
        q: "Do examiners give partial marks for wrong final answers?",
        a: "Yes, if the method is shown clearly. Skipping working to write only the final number is the fastest way to zero out a partially-correct answer.",
      },
      {
        q: "How important are graphs in numerical questions?",
        a: "In many DU papers, a labelled graph is explicitly worth 2–3 marks even when the question doesn't mention it. Drawing one is almost always worth the minute it takes.",
      },
      {
        q: "Should I round intermediate steps?",
        a: "No. Round only at the final answer, to the significant figures the question implies. Rounding early is a common source of otherwise-avoidable lost marks.",
      },
    ],
  },
  "mme-du-economics-guide": {
    seoTitle: "DU MME 1 & 2 Guide: Syllabus, Solved Examples, PYQs",
    metaDescription:
      "MME 1 and MME 2 at Delhi University, unpacked: syllabus map, solved Lagrangian and optimisation examples, past paper patterns, and a twelve-week plan.",
    focusKeyword: "MME 1 DU Economics Honours notes",
    secondaryKeywords: [
      "Mathematical Methods for Economics DU",
      "MME 2 DU syllabus",
      "DU Economics Honours maths",
      "Sydsaeter Hammond DU",
      "Lagrangian solved example DU",
    ],
    lsiKeywords: [
      "constrained optimisation",
      "bordered Hessian",
      "Cobb-Douglas utility",
      "partial derivatives",
      "Cramer's rule",
      "eigenvalues",
      "consumer surplus integration",
    ],
    keyTakeaways: [
      "MME 1 builds the single-variable calculus that every later economics paper relies on; do not treat it as a service course.",
      "MME 2 is the same machinery in higher dimensions, plus enough linear algebra to solve the systems it produces.",
      "Three question patterns repeat every year: one long optimisation, a short-answer definitions block, and an integration or difference-equation problem.",
      "The last two marks on optimisation questions almost always come from the second-order check and the economic interpretation of the multiplier.",
      "A working note set has four parts: definitions, formulas with conditions, worked examples, and a past-paper index tagged by topic.",
    ],
    faqs: [
      {
        q: "Is MME 1 harder than Class 12 maths?",
        a: "The techniques overlap, but MME asks you to prove and interpret, not just compute. A student comfortable with Class 12 calculus still has real work to do on limits, continuity, and the economic reading of an optimisation problem.",
      },
      {
        q: "Which textbook does DU actually follow for MME?",
        a: "Sydsaeter, Hammond, Strom and Carvajal's Essential Mathematics for Economic Analysis is the prescribed text and the paper is built from it. Chiang and Wainwright is a good second book for the economic interpretation.",
      },
      {
        q: "How many past papers should I solve before the exam?",
        a: "At minimum the last five years, closed book, under time. A student aiming for a first-class score usually solves ten and tags every question by topic to expose weak blocks.",
      },
      {
        q: "Do I need to memorise proofs for MME?",
        a: "You need to reproduce short arguments, not full formal proofs. The mean value theorem, Young's theorem, and the classification of extrema by the second-order condition are the ones worth rehearsing in words.",
      },
      {
        q: "Is the eigenvalue block really important for MME 2?",
        a: "Yes. It carries marks in MME 2 and returns in Intermediate Macro when you study the stability of a linear dynamic system. Learning it once, cleanly, pays off twice.",
      },
    ],
  },
  "du-csas-round-2-allotment-2026": {
    seoTitle: "DU CSAS Round 2 Allotment 2026: Deadlines & Rules",
    metaDescription:
      "Delhi University's CSAS Round 2 allotment on July 25, 2026 gave 24,814 fresh seats. Deadlines, upgrade rules, and what admitted students should do before July 28.",
    focusKeyword: "DU CSAS Round 2 2026",
    secondaryKeywords: [
      "Delhi University seat allotment 2026",
      "CSAS UG round 2",
      "DU admission July 2026",
      "admission.uod.ac.in",
    ],
    lsiKeywords: ["upgrade", "freeze seat", "fee payment", "spot round", "orientation"],
    keyTakeaways: [
      "DU released the CSAS Round 2 UG allotment on July 25, 2026 with 24,814 fresh allotments.",
      "Accept by 4:59 pm July 26, verification by July 27, fees by July 28. Missing any cut-off forfeits the seat.",
      "The upgrade option never loses your current seat, only replaces it if a higher preference opens up.",
      "Classes for 2026-27 begin on July 28 at the currently allotted college, regardless of pending upgrades.",
    ],
    faqs: [
      {
        q: "What is the deadline to accept a CSAS Round 2 seat?",
        a: "Candidates must accept the allotted seat online by 4:59 pm on July 26, 2026. Colleges verify documents till 4:59 pm on July 27 and fee payment closes at 4:59 pm on July 28.",
      },
      {
        q: "Can I change my preference order after Round 1?",
        a: "No. Preferences are locked at registration. Upgrades work strictly on the preference order you submitted, and cannot be reordered later.",
      },
      {
        q: "Will there be a Round 3?",
        a: "A spot round is possible if seats remain unfilled after Round 2, but it is not guaranteed and it does not offer further upgrades to already admitted students.",
      },
      {
        q: "What happens if I keep the upgrade option on?",
        a: "Your current seat stays confirmed. If a higher preference opens up in a later round, you are moved there automatically. There is no risk of losing the current seat.",
      },
    ],
  },
  "du-direct-phd-four-year-ug": {
    seoTitle: "DU Direct PhD After 4-Year UG: Eligibility & CGPA",
    metaDescription:
      "Delhi University's Academic Council has approved direct PhD admission for four-year UG graduates under UGCF 2022. Eligibility, CGPA cut-off and what it means for students.",
    focusKeyword: "DU direct PhD four-year UG",
    secondaryKeywords: [
      "UGCF 2022 PhD",
      "DU PhD admission 2026",
      "FYUP research year",
      "NEP 2020 PhD",
    ],
    lsiKeywords: ["entrance test", "CGPA 7.5", "honours with research", "doctoral programme"],
    keyTakeaways: [
      "The July 25, 2026 Academic Council approved direct PhD entry for students completing the four-year UG with research under UGCF 2022.",
      "Minimum eligibility is a CGPA of 7.5 in the overall programme, plus the departmental entrance test and interview.",
      "Students exiting after three years with honours still need a master's degree for PhD admission.",
      "The rule applies to DU PhD admissions only; IITs, IISc, JNU and other institutes retain their own eligibility norms.",
    ],
    faqs: [
      {
        q: "Do I still need to write a PhD entrance test?",
        a: "Yes. The entrance test remains compulsory, and carries 70 per cent of the selection weight. The interview accounts for the remaining 30 per cent.",
      },
      {
        q: "What CGPA do I need to qualify?",
        a: "A minimum overall CGPA of 7.5, or its percentage equivalent, in the four-year UG programme with the research specialisation in the fourth year.",
      },
      {
        q: "Does an honours degree without the fourth year qualify?",
        a: "No. Three-year honours graduates still need a master's degree to be eligible for DU's PhD programmes.",
      },
      {
        q: "When will the first PhD cycle under this rule open?",
        a: "The next PhD advertisement is expected in October 2026 with entrances in November-December 2026, in line with DU's usual annual cycle.",
      },
    ],
  },
  "du-sol-renamed-cdoe-sports-scholarship": {
    seoTitle: "DU SOL Renamed CDOE + Arun Jaitley Sports Scholarship",
    metaDescription:
      "Delhi University's School of Open Learning becomes the Campus of Distance and Online Education, and a new Arun Jaitley Sports Scholarship is announced.",
    focusKeyword: "SOL renamed to CDOE",
    secondaryKeywords: [
      "Arun Jaitley Sports Scholarship",
      "DU distance learning 2026",
      "Campus of Distance and Online Education",
      "DU sports quota",
    ],
    lsiKeywords: ["UGC ODL", "hybrid learning", "All India Inter-University", "DUSU"],
    keyTakeaways: [
      "The School of Open Learning has been renamed the Campus of Distance and Online Education (CDOE).",
      "Existing SOL degrees remain valid under their current title; ongoing programmes continue without disruption.",
      "The Arun Jaitley Sports Scholarship supports students representing DU at All India Inter-University level or higher.",
      "The scholarship is separate from sports-quota admissions; it funds students already on the rolls.",
    ],
    faqs: [
      {
        q: "Are current SOL degrees still valid after the rename?",
        a: "Yes. Degrees earned under the SOL name remain valid and require no reissue. The change is limited to the institution's new title going forward.",
      },
      {
        q: "Who can apply for the Arun Jaitley Sports Scholarship?",
        a: "Students who represent DU at the All India Inter-University level or higher, including international events, are eligible. Detailed criteria will be notified separately.",
      },
      {
        q: "Does the rename affect course fees or examination schedules?",
        a: "No. Fee structures and examination calendars for SOL programmes continue unchanged during the transition.",
      },
      {
        q: "Is CDOE a fully online mode?",
        a: "No. It combines online content, live tutorials and periodic on-campus contact, in line with UGC's open and distance learning regulations.",
      },
    ],
  },
  "du-academic-council-qs-skill-courses": {
    seoTitle: "DU New Skill Courses 2027 & QS World Ranking Rise",
    metaDescription:
      "DU's July 25 Academic Council added new UGCF 2022 skill courses in data, communication and wellbeing, and recorded a higher QS World Ranking 2027.",
    focusKeyword: "DU skill courses UGCF 2022",
    secondaryKeywords: [
      "DU QS Ranking 2027",
      "SEC and VAC courses",
      "SAMARTH portal DU",
      "Academic Council July 2026",
    ],
    lsiKeywords: ["Python", "financial literacy", "employer reputation", "wellbeing"],
    keyTakeaways: [
      "The Academic Council cleared new skill and value-addition courses under UGCF 2022, starting January 2027.",
      "New titles include Introduction to Data Analysis with Python, Applied Statistics, and Digital Communication.",
      "Skill papers count toward credit but are graded separately from the core discipline paper.",
      "DU's improved QS World University Ranking 2027 mainly reflects employer and academic reputation scores.",
    ],
    faqs: [
      {
        q: "When do the new skill courses become available?",
        a: "From the January 2027 semester for continuing students, and from semester one for the incoming batch.",
      },
      {
        q: "Where do I register for a skill course?",
        a: "Registration happens on the SAMARTH portal in the first week of the semester, after checking which courses your specific college is running.",
      },
      {
        q: "Does a low grade in a skill paper hurt my CGPA a lot?",
        a: "Skill papers carry credit and appear on the transcript, but they carry less weight than a core discipline paper of the same size. A weak grade drags the CGPA less, but it is still visible.",
      },
      {
        q: "How much does the QS ranking movement change my job prospects?",
        a: "Marginally. Rankings reflect employer perception and are useful for global applications, but recruiters mostly read the transcript, the projects and the college within DU, not the rank line.",
      },
    ],
  },
  "du-long-answer-question-playbook": {
    seoTitle: "DU Economics Long Answer Playbook: Score 12+ on 15 Marks",
    metaDescription:
      "How to write a 15-mark long answer in DU economics that scores in the top band. Four-part structure, graph tips, time budget and worked IS-LM example.",
    focusKeyword: "DU economics long answer questions",
    secondaryKeywords: [
      "15 mark question answer format",
      "how to write long answers economics",
      "DU exam answer structure",
      "economics honours answer writing",
      "long answer strategy DU",
    ],
    lsiKeywords: [
      "IS-LM model answer",
      "step marking",
      "graph labelling",
      "definition opening",
      "evaluative conclusion",
      "past paper practice",
    ],
    keyTakeaways: [
      "A 15-mark answer is a four-band contract: define, derive, apply, evaluate. Missing any one band caps your score.",
      "Spend two minutes planning four bullet points before writing. It routinely adds two to three marks.",
      "Every graph should have labelled axes, labelled curves, a marked equilibrium and one movement arrow.",
      "One specific real-world example beats three vague ones. Concreteness is what the top band rewards.",
      "Budget 35 to 40 minutes per 15-marker on a three-hour paper, including a two-minute reread.",
    ],
    faqs: [
      {
        q: "How long should a 15-mark answer be in a DU economics exam?",
        a: "Roughly three to four A4 sides of a standard answer booklet, including one or two labelled graphs. Longer answers rarely score more; structure matters more than length.",
      },
      {
        q: "Do I need to draw a graph for every long-answer question?",
        a: "Draw one whenever the model admits it, even if the question does not explicitly ask. In DU papers, a properly labelled graph is typically worth two to three marks on its own.",
      },
      {
        q: "Should I write in paragraphs or points for long answers?",
        a: "Paragraphs for the definition and evaluation bands, numbered steps for the derivation, and short paragraphs for the application. Pure bullet-point answers usually score lower.",
      },
      {
        q: "How many past papers should I practise before the exam?",
        a: "Solve the last three years of long-answer sections under strict time. Two full papers written and self-marked against the four-band contract is the highest-return preparation you can do.",
      },
      {
        q: "What is the biggest mistake students make on 15-mark questions?",
        a: "Skipping the evaluation paragraph. Students write strong definitions and derivations, run out of time, and end abruptly. The evaluative close is where the top band of marks is decided.",
      },
    ],
  },
  "du-csas-round-2-schedule-revised-2026": {
    seoTitle: "DU CSAS Round 2 2026: Schedule Revised, Accept by July 27",
    metaDescription:
      "DU has revised the CSAS Round 2 2026 schedule. Deadline to accept the seat is now July 27 and fee payment July 28. Here is what the new timeline means for admitted students.",
    focusKeyword: "DU CSAS round 2 schedule revised 2026",
    secondaryKeywords: [
      "DU UG admission 2026 round 2",
      "CSAS round 2 accept seat deadline",
      "DU seat allotment July 27",
      "admission.uod.ac.in round 2",
    ],
    lsiKeywords: [
      "CUET",
      "seat upgrade",
      "college verification",
      "fee payment deadline",
      "Round 3 tentative",
    ],
    keyTakeaways: [
      "Deadline to accept a CSAS Round 2 seat is now 11.59 pm on July 27, 2026.",
      "Fee payment window closes at 11.59 pm on July 28, 2026 after college approval.",
      "Round 2 released 24,814 fresh allotments across DU colleges.",
      "Doing nothing on the portal is not the same as declining an upgrade.",
    ],
    faqs: [
      {
        q: "What is the new deadline for CSAS Round 2 seat acceptance?",
        a: "The revised deadline to accept the allotted seat is 11.59 pm on Sunday, July 27, 2026. Fee payment closes at 11.59 pm on Tuesday, July 28, 2026.",
      },
      {
        q: "What happens if I miss the acceptance window?",
        a: "Missing the window forfeits the Round 2 allotment. If the allotment was an upgrade, you may also lose the earlier Round 1 seat in some cases. Act on the portal even to decline.",
      },
      {
        q: "When is CSAS Round 3 expected?",
        a: "DU has indicated a Round 3 in the last week of July for candidates without an allotment or with a freed seat. The exact notification will appear on du.ac.in.",
      },
    ],
  },
  "one-page-cheat-sheets-that-work": {
    seoTitle: "One-Page Cheat Sheets That Save You in the Exam (2026)",
    metaDescription:
      "How to build a single A4 sheet that carries an entire theory-heavy semester. Layout, three-pass method, what to leave out, and how to drill it before the exam.",
    focusKeyword: "one page cheat sheet study",
    secondaryKeywords: [
      "how to make revision notes",
      "one page summary notes",
      "last minute revision technique",
      "cheat sheet for exams",
      "study sheet layout",
    ],
    lsiKeywords: [
      "active recall",
      "spaced repetition",
      "revision strategy",
      "DU exam preparation",
      "handwritten notes",
      "condensed notes",
    ],
    keyTakeaways: [
      "A single A4 sheet forces prioritisation in a way thick notes never do.",
      "Six panels, five for units and one cross-cutting, is the layout that keeps working.",
      "Build the sheet in three passes: from the syllabus, from memory, then corrected.",
      "The value is in making the sheet, not in reading someone else's photocopy.",
    ],
    faqs: [
      {
        q: "Is a one-page cheat sheet enough for an entire subject?",
        a: "For theory-heavy papers, yes. For numerical papers like statistics or mathematical methods, use a two-page variant with the second page reserved for worked formulae.",
      },
      {
        q: "When should I start making the sheet?",
        a: "Two to three weeks before the exam. It needs at least three passes over that period; a rushed one-night version misses the point of the exercise.",
      },
      {
        q: "Should I type the sheet or handwrite it?",
        a: "Handwrite it. The slower pace of copying by hand consolidates the material in a way typing does not.",
      },
      {
        q: "Can I use a senior's cheat sheet instead of making my own?",
        a: "Read theirs for the layout idea, then make your own from scratch. The sheet's value is in the act of building it, not in the artefact itself.",
      },
    ],
  },

  "du-csas-round-3-spot-admission-2026": {
    seoTitle: "DU CSAS Round 3 & Spot Admission 2026: Vacant Seats Guide",
    metaDescription:
      "DU CSAS Round 3 and Spot Admission 2026: vacant seat matrix, registration dates on admission.uod.ac.in, tie-breaking rules and how to order your choices.",
    focusKeyword: "DU CSAS Round 3 Spot Admission 2026",
    secondaryKeywords: ["DU vacant seat matrix 2026", "CSAS UG spot round registration", "DU tie breaking rules 2026"],
    lsiKeywords: ["admission.uod.ac.in", "CUET UG score cutoff", "Hindu College vacant seats"],
    keyTakeaways: [
      "DU CSAS Round 3 and Spot Allocation schedule published on admission.uod.ac.in.",
      "Vacant seat matrix determines available choices for unallotted candidates.",
      "Tie-breaking rules prioritize Class 12 Best 3, Best 4, Best 5 marks and candidate age.",
    ],
    faqs: [
      {
        q: "When will DU CSAS Round 3 vacant seats be published?",
        a: "Immediately following the reconciliation of Round 2 online fee payments on admission.uod.ac.in.",
      },
      {
        q: "Can I upgrade my seat during Spot Allocation?",
        a: "No, candidates accepting a seat in Spot Allocation cannot upgrade in subsequent regular rounds.",
      },
    ],
  },

  "cuet-pg-economics-2026-cutoff-dse-jnu": {
    seoTitle: "CUET PG Economics 2026 Cutoff: DSE, JNU CESP Score Guide",
    metaDescription:
      "CUET PG Economics 2026 cutoffs for DSE Delhi, JNU CESP and other universities, safe raw scores, deprivation points and COQP10 syllabus coverage.",
    focusKeyword: "CUET PG Economics 2026 Cutoff DSE JNU",
    secondaryKeywords: ["DSE MA Economics cutoff 2026", "JNU CESP deprivation points", "COQP10 syllabus"],
    lsiKeywords: ["econdse.org", "jnu.ac.in", "CUET PG raw score vs rank"],
    keyTakeaways: [
      "DSE Unreserved safe raw score target stands at 245+ out of 300 marks.",
      "JNU awards up to 12 Deprivation Points for Quartile 1 districts and female candidates.",
    ],
    faqs: [
      {
        q: "What is a safe score for DSE MA Economics in CUET PG 2026?",
        a: "A score of 245+ for UR candidates and 225+ for OBC-NCL candidates provides a strong safe margin.",
      },
    ],
  },

  "rbi-grade-b-depr-2026-preparation-guide": {
    seoTitle: "RBI Grade B DEPR 2026: Syllabus, Papers & Prep Guide",
    metaDescription:
      "How to prepare for RBI Grade B DEPR 2026: Phase 1 and Phase 2 syllabus, descriptive paper strategy, previous year papers and MA Economics mapping.",
    focusKeyword: "RBI Grade B DEPR 2026 Preparation",
    secondaryKeywords: ["RBI DEPR syllabus PDF", "DSE economics to RBI DEPR", "RBI DEPR previous year papers"],
    lsiKeywords: ["rbi.org.in", "Monetary Policy Committee", "descriptive economics paper"],
    keyTakeaways: [
      "RBI DEPR Phase 1 tests objective Micro, Macro, Econometrics, and English.",
      "Phase 2 descriptive paper maps directly to DSE and DU Economics Honours syllabi.",
    ],
    faqs: [
      {
        q: "Is an MA in Economics compulsory for RBI Grade B DEPR?",
        a: "Yes, a Master's degree in Economics or Quantitative Finance is required.",
      },
    ],
  },

  "du-exam-revaluation-rechecking-fees-2026": {
    seoTitle: "DU Revaluation & Rechecking 2026: Fees, Forms, Timeline",
    metaDescription:
      "DU exam revaluation and rechecking 2026 explained: fees per paper, how to apply on exam.du.ac.in, photocopy requests and how long results take.",
    focusKeyword: "DU Exam Revaluation Rechecking Fees 2026",
    secondaryKeywords: ["DU exam revaluation online form", "DU rechecking fee per paper", "photocopy answer script DU"],
    lsiKeywords: ["exam.du.ac.in", "Ordinance IX", "revaluation vs rechecking"],
    keyTakeaways: [
      "Revaluation costs ₹1,000 per paper for fresh answer sheet evaluation.",
      "Rechecking costs ₹750 per paper for mark summation verification.",
    ],
    faqs: [
      {
        q: "How long does DU take to release revaluation results?",
        a: "Revaluation results are published on exam.du.ac.in within 45 to 60 days of application.",
      },
    ],
  },

  "du-internal-assessment-grace-marks-guidelines-2026": {
    seoTitle: "DU Internal Assessment Marks 2026: Slabs & Grievance Rules",
    metaDescription:
      "DU internal assessment and grace marks guidelines 2026: attendance mark slabs, IAMC grievance process and how UGCF continuous assessment is split.",
    focusKeyword: "DU Internal Assessment Grievance Guidelines 2026",
    secondaryKeywords: ["DU IA mark slabs attendance", "IAMC grievance committee DU", "continuous assessment UGCF"],
    lsiKeywords: ["UGCF 2022 credit split", "attendance detention waiver", "internal marks discrepancy"],
    keyTakeaways: [
      "Attendance marks slab gives 5 marks for 85%+ attendance and 1 mark for 66.6%.",
      "Grievances must be submitted to the college IAMC before final mark locking.",
    ],
    faqs: [
      {
        q: "What is the minimum attendance required to get attendance marks in DU?",
        a: "66.6% attendance yields 1 mark, while 85% and above yields full 5 marks.",
      },
    ],
  },

  "cobb-douglas-production-function-proofs": {
    seoTitle: "Cobb-Douglas Production Function: Proofs & Properties",
    metaDescription:
      "Cobb-Douglas production function explained with proofs: Euler theorem, homogeneity, returns to scale, marginal products and elasticity of substitution.",
    focusKeyword: "Cobb Douglas Production Function Proofs",
    secondaryKeywords: ["Euler theorem Cobb Douglas proof", "returns to scale Cobb Douglas", "elasticity of substitution Cobb Douglas"],
    lsiKeywords: ["marginal product of labor", "homogeneous function degree", "du microeconomics exam"],
    keyTakeaways: [
      "Cobb-Douglas technology is homogeneous of degree r = alpha + beta.",
      "Euler's theorem proves product exhaustion under constant returns to scale.",
    ],
    faqs: [
      {
        q: "What is the elasticity of substitution for a Cobb-Douglas production function?",
        a: "It is strictly equal to 1 everywhere along the isoquant.",
      },
    ],
  },

  "mundell-fleming-model-fixed-vs-floating": {
    seoTitle: "Mundell-Fleming Model: Fixed vs Floating Exchange Rates",
    metaDescription:
      "Mundell-Fleming IS-LM-BP model compared under fixed and floating exchange rates: policy effectiveness, capital mobility and the Impossible Trinity.",
    focusKeyword: "Mundell Fleming Model Fixed vs Floating Exchange Rates",
    secondaryKeywords: ["IS LM BP model open economy", "policy ineffectiveness theorem", "Impossible Trinity trilemma"],
    lsiKeywords: ["perfect capital mobility", "r equals r star", "exchange rate crowding out"],
    keyTakeaways: [
      "Fiscal policy is maximally effective under fixed exchange rates and ineffective under floating.",
      "Monetary policy is maximally effective under floating exchange rates and ineffective under fixed.",
    ],
    faqs: [
      {
        q: "Why is fiscal policy ineffective under floating exchange rates?",
        a: "Because increased interest rates cause currency appreciation, crowding out net exports completely.",
      },
    ],
  },

  "igidr-mumbai-vs-dse-ma-economics-2026": {
    seoTitle: "IGIDR Mumbai vs DSE Delhi: MA Economics Comparison 2026",
    metaDescription:
      "IGIDR Mumbai vs DSE Delhi for MA Economics 2026: fees, stipends, hostel, placement records, entrance patterns and which institute suits your goals.",
    focusKeyword: "IGIDR Mumbai vs DSE MA Economics 2026",
    secondaryKeywords: ["IGIDR MSc economics placement", "DSE vs IGIDR stipend hostel", "IGIDR entrance exam pattern"],
    lsiKeywords: ["RBI funded institute", "econdse.org", "quantitative economics master"],
    keyTakeaways: [
      "IGIDR guarantees monthly stipends (₹12k-₹17k) and 100% hostel accommodation.",
      "DSE provides higher corporate batch strength and extensive alumni network.",
    ],
    faqs: [
      {
        q: "Does IGIDR Mumbai provide a monthly stipend to MSc students?",
        a: "Yes, all admitted MSc Economics students receive a monthly stipend funded by RBI.",
      },
    ],
  },

  "du-ge-sec-vac-course-selection-2026": {
    seoTitle: "DU GE, SEC & VAC Elective Selection Guide 2026",
    metaDescription:
      "How to choose GE, SEC and VAC electives at DU in 2026: high scoring options, CGPA impact, SAMARTH selection steps and best GE for Economics Honours.",
    focusKeyword: "DU Elective Selection GE SEC VAC 2026",
    secondaryKeywords: ["best GE for economics honours DU", "high scoring SEC VAC subjects", "SAMARTH elective selection"],
    lsiKeywords: ["UGCF 2022 minor discipline", "4 credit GE paper", "cgpa booster subjects DU"],
    keyTakeaways: [
      "Maths GE satisfies quantitative prerequisites for DSE and ISI admissions.",
      "Practical SEC/VAC papers offer high continuous assessment scoring opportunities.",
    ],
    faqs: [
      {
        q: "Can I change my Generic Elective (GE) subject in Semester 2?",
        a: "Yes, DU allows students to choose different GE subjects across semesters.",
      },
    ],
  },

  "how-to-calculate-sgpa-to-cgpa-in-du-2026": {
    seoTitle: "How to Calculate SGPA to CGPA in DU 2026: Formula & Example",
    metaDescription:
      "Step-by-step DU SGPA and CGPA calculation using the credit-weighted formula, the grade point scale, and the official CGPA to percentage method.",
    focusKeyword: "How to Calculate SGPA to CGPA in DU 2026",
    secondaryKeywords: ["DU CGPA to percentage formula 9.5", "SGPA formula UGCF 2022", "letter grade point scale DU"],
    lsiKeywords: ["credit weighted average", "transcript verification DU", "Outstanding grade 10 points"],
    keyTakeaways: [
      "SGPA is calculated as the sum of credit-weighted grade points divided by total credits.",
      "Official DU equivalent percentage formula multiplies CGPA by 9.5.",
    ],
    faqs: [
      {
        q: "What is the formula to convert DU CGPA into percentage?",
        a: "Multiply your CGPA by 9.5 (Percentage = CGPA x 9.5).",
      },
    ],
  },

  "du-first-year-survival-guide-2026": {
    seoTitle: "DU First Year Survival Guide 2026: Freshers Checklist & Tips",
    metaDescription:
      "Exhaustive guide for DU freshers: document verification checklist, SAMARTH portal, orientation, society selection, GE/SEC/VAC balance, and attendance rules.",
    focusKeyword: "DU first year survival guide 2026",
    secondaryKeywords: ["what to do after DU admission", "DU freshers orientation 2026", "DU document verification checklist", "DU college life guide"],
    lsiKeywords: ["UGCF 2022 credit system", "SAMARTH portal DU", "66.6 attendance rule", "college societies DU"],
    keyTakeaways: [
      "Prepare 2 self-attested document sets with OBC-NCL/EWS certificates issued on or after April 1, 2026.",
      "Attendance is mandatory at 66.6% minimum under DU Ordinance VII.",
      "Limit active society participation to 1 core career society + 1 creative society in Semester 1.",
    ],
    faqs: [
      {
        q: "Is attendance mandatory on DU orientation day?",
        a: "Yes. Departmental orientation is where elective preference forms and tutorial section assignments are distributed.",
      },
      {
        q: "What documents are required for physical verification at DU colleges?",
        a: "Class 10 mark sheet, Class 12 mark sheet, CUET scorecard, category certificate (if applicable), fee receipt, character certificate, and Aadhaar card.",
      },
    ],
  },

  "du-cuet-ug-2026-college-wise-cutoff-score-predictor": {
    seoTitle: "DU CUET UG 2026 Cutoff: College Wise Score Predictor",
    metaDescription:
      "Comprehensive analysis of DU CUET UG 2026 cutoffs, Round 1 vs Round 2 safe scores, category ranks, and 3-tier CSAS preference filling strategy.",
    focusKeyword: "DU CUET UG 2026 college wise cutoff",
    secondaryKeywords: ["DU college predictor 2026", "CUET score vs DU college", "CSAS round 2 cutoff DU", "SRCC cutoff 2026"],
    lsiKeywords: ["NTA normalized score", "equipercentile method", "DU tie breaking rules", "CSAS preference list"],
    keyTakeaways: [
      "DU uses NTA normalized scores, not raw marks, to compile course merit lists.",
      "SRCC, St. Stephen's, and Hindu B.A. Economics cutoffs require 778–795+ normalized scores.",
      "Never alter preference order based on your score; always arrange in strict dream-to-safe hierarchy.",
    ],
    faqs: [
      {
        q: "Does clicking 'Upgrade' on the CSAS portal cancel my current seat?",
        a: "No. Your current seat remains 100% secure unless a higher preference seat is successfully allocated.",
      },
      {
        q: "What is a safe CUET score for North Campus BA Economics Hons?",
        a: "A normalized score of 760+ out of 800 provides a strong safe margin for General Category candidates.",
      },
    ],
  },

  "du-pg-spot-admission-2026-vacant-seats-guide": {
    seoTitle: "DU PG Spot Admission 2026: Vacant Seats & Process Guide",
    metaDescription:
      "Operational guide on DU PG Spot Admission 2026. Vacant seat matrix, CSAS PG opt-in registration, and CUET PG cutoff thresholds for MA Economics, Pol Sci, and MSc.",
    focusKeyword: "DU PG spot admission 2026",
    secondaryKeywords: ["DU MA vacant seats 2026", "CSAS PG spot round registration", "DSE MA Economics spot cutoff", "DU PG admission schedule"],
    lsiKeywords: ["pgadmission.uod.ac.in", "CUET PG paper code COQP10", "DSE vacant seats", "department verification"],
    keyTakeaways: [
      "Unallotted candidates must explicitly opt-in on pgadmission.uod.ac.in for Spot Round consideration.",
      "Already admitted PG candidates cannot participate or upgrade during Spot Admission.",
      "Spot Round 1 allocation results declared on July 29, 2026 with fee payment deadline August 2, 2026.",
    ],
    faqs: [
      {
        q: "Can already admitted DU PG students participate in Spot Admission?",
        a: "No. Candidates who have already paid admission fees in regular rounds are barred from Spot Allocation.",
      },
      {
        q: "What is the CUET PG safe score for MA Economics DSE in Spot Round?",
        a: "A score of 228–235 out of 300 serves as a safe threshold for Unreserved candidates in Spot Allocation.",
      },
    ],
  },

  "du-hostel-admission-2026": {
    seoTitle: "DU Hostel Admission 2026: Online Form, Merit & Deadlines",
    metaDescription:
      "Comprehensive guide on Delhi University hostel admission 2026. Covering application process, merit lists, distance criteria, document requirements, and deadlines.",
    focusKeyword: "DU hostel admission 2026",
    secondaryKeywords: ["Delhi university hostel accommodation form", "DU hostel last date 2026", "DU hostel fees 2026"],
    lsiKeywords: ["college specific hostels", "university halls of residence", "local guardian documentation"],
    keyTakeaways: [
      "Online application deadline for DU hostel admissions is August 5, 2026.",
      "Allocation is based on CUET merit and distance from Delhi/NCR.",
      "SOL and NCWEB students are ineligible for university hostel seats.",
    ],
    faqs: [
      {
        q: "What is the last date to apply for DU hostels 2026?",
        a: "The online application window closes on August 5, 2026.",
      },
      {
        q: "Can local Delhi students apply for DU hostels?",
        a: "Generally no, hostels prioritize outstation students based on a minimum distance radius criteria.",
      },
    ],
  },

  "du-cost-of-living-pg-vs-hostel-2026": {
    seoTitle: "DU Cost of Living 2026: North Campus PG vs Hostel Rent",
    metaDescription:
      "Breakdown of the cost of living for Delhi University students in 2026. Compare North Campus PG rents, hostel fees, flat sharing, and hidden lifestyle expenses.",
    focusKeyword: "DU North Campus PG rent 2026",
    secondaryKeywords: ["cost of living Delhi University students", "DU hostel vs PG comparison", "budgeting for DU freshers"],
    lsiKeywords: ["Kamla Nagar rent", "Hudson Lane PGs", "flat sharing North Campus"],
    keyTakeaways: [
      "A realistic monthly budget for North Campus living in 2026 ranges from ₹15,000 to ₹20,000.",
      "While DU hostels cost ₹4,000-₹6,000 per month, private PGs range from ₹9,500 to ₹19,000+ depending on amenities.",
      "Always account for hidden costs like sub-metered electricity and security deposits when booking a PG.",
    ],
    faqs: [
      {
        q: "How much does a PG cost in DU North Campus?",
        a: "In 2026, PG rents in North Campus range from ₹8,000 for basic triple sharing to over ₹22,000 for premium single rooms.",
      },
      {
        q: "Is living in a shared flat cheaper than a PG in Delhi?",
        a: "It can be cheaper per head (₹5,000-₹8,000 rent), but requires managing groceries, cook salary, and utilities independently.",
      },
    ],
  },

  "du-eca-sports-quota-admission-2026": {
    seoTitle: "DU ECA & Sports Quota Admission 2026: Trial Schedule & Merit",
    metaDescription:
      "Analysis of Delhi University ECA and Sports Quota admissions 2026. Discover physical trial schedules, the 75-25 merit calculation formula, and allocation timelines.",
    focusKeyword: "DU ECA quota admission 2026",
    secondaryKeywords: ["DU sports quota trials July 2026", "Delhi university sports quota merit list", "CSAS ECA allocation"],
    lsiKeywords: ["75-25 weightage rule", "physical trial performance", "forfeiture rule DU"],
    keyTakeaways: [
      "Final merit is a hybrid score: 75% physical trial performance and 25% CUET UG score.",
      "ECA and Sports allocations occur after CSAS Round 3.",
      "Accepting a quota seat automatically forfeits any previously allotted regular CSAS seat.",
    ],
    faqs: [
      {
        q: "What is the weightage of the CUET score in DU Sports quota?",
        a: "The CUET UG score carries a 25% weightage, while the physical trial and certificates carry 75%.",
      },
      {
        q: "Can I keep my regular DU seat if I get a Sports quota seat?",
        a: "If you choose to ACCEPT the Sports quota seat, your regular seat is forfeited. If you ignore the Sports offer, you keep your regular seat.",
      },
    ],
  },

  "du-csas-round-3-mid-entry-schedule-2026": {
    seoTitle: "DU CSAS Round 3, Mid-Entry & ECA Schedule 2026",
    metaDescription:
      "Official DU UG Admissions 2026 schedule. Details on Upgrade Window (Aug 1), Mid-Entry (Aug 3-5), Round 3 CSAS (Aug 8), and ECA/Sports Allocation dates.",
    focusKeyword: "DU CSAS Round 3 schedule 2026",
    secondaryKeywords: ["DU Mid-Entry 2026 date", "Delhi University upgrade allocation", "DU ECA allocation date", "CSAS third round 2026"],
    lsiKeywords: ["Ward Quota Round 1", "Correction Window DU", "Performance-Based Programmes"],
    keyTakeaways: [
      "Mid-Entry runs from August 3 to August 5, 2026, allowing late registrations at the cost of being placed below regular applicants in merit.",
      "Round 3 CSAS Allocation for regular seats will be declared on August 8, 2026 at 5:00 PM.",
      "ECA, Sports, and Performance-Based allocation lists will be published on August 12, 2026.",
    ],
    faqs: [
      {
        q: "When will DU CSAS Round 3 results be declared in 2026?",
        a: "The CSAS Round 3 allocation list will be published on August 8, 2026, at 5:00 PM.",
      },
      {
        q: "What is the disadvantage of DU Mid-Entry?",
        a: "A Mid-Entry candidate is always placed below all candidates who applied during the regular phases, regardless of their CUET score.",
      },
    ],
  },

  "du-fee-refund-policy-cancellation-2026": {
    seoTitle: "DU Fee Refund Policy 2026: Admission Cancellation Guide",
    metaDescription:
      "Understand the Delhi University (DU) Fee Refund Policy 2026. Step-by-step guide to cancelling your admission via CSAS portal and 100% vs 50% refund timelines.",
    focusKeyword: "DU fee refund policy 2026",
    secondaryKeywords: ["Delhi university admission cancellation process", "how to withdraw DU admission", "CSAS withdrawal fee 2026"],
    lsiKeywords: ["100% refund UGC guidelines", "fake documents no refund", "DU fee reversal timeline"],
    keyTakeaways: [
      "You receive a 100% refund (minus processing fees) if you withdraw on or before the official Last Date of Admission.",
      "Withdrawal within 30 days after the Last Date of Admission results in a 50% refund.",
      "Refunds are processed back to the original source of payment, usually taking 2-4 months after the admission cycle closes.",
    ],
    faqs: [
      {
        q: "Can I get a full fee refund from Delhi University if I cancel my admission?",
        a: "Yes, you get a 100% refund of academic fees if you formally withdraw through the CSAS portal on or before the notified Last Date of Admission.",
      },
      {
        q: "How long does DU take to refund the admission fee?",
        a: "Typically, DU processes refunds 2 to 4 months after the entire admission cycle concludes.",
      },
    ],
  },

  "du-sol-admission-without-cuet-2026": {
    seoTitle: "DU SOL Admission 2026: Apply Without CUET, Eligibility & Fees",
    metaDescription:
      "Guide to Delhi University School of Open Learning (DU SOL) admissions 2026. Secure a DU degree without CUET, eligibility criteria, and step-by-step application.",
    focusKeyword: "DU SOL admission 2026",
    secondaryKeywords: ["Delhi University school of open learning last date", "DU admission without CUET 2026", "CDOE Delhi University"],
    lsiKeywords: ["distance learning DU", "SOL study material distribution", "PCP classes attendance"],
    keyTakeaways: [
      "DU SOL (now CDOE) does not require a CUET score; admissions are based purely on Class 12th merit.",
      "You can pursue a SOL degree simultaneously with another regular degree as per NEP 2020 guidelines.",
      "Registration is entirely online at sol.du.ac.in and usually stays open until late August/September.",
    ],
    faqs: [
      {
        q: "Do I need to give CUET for DU SOL admission?",
        a: "No. Admission to DU SOL undergraduate programs is direct, based on your Class 12 board examination marks.",
      },
      {
        q: "Is a DU SOL degree equal to a regular DU degree?",
        a: "Yes, the DU SOL degree is officially equivalent to a regular DU degree and is valid for all postgraduate and competitive exams like UPSC.",
      },
    ],
  },

  "du-academic-calendar-exam-dates-2026": {
    seoTitle: "DU Academic Calendar 2026-27: Sem 1 Exam Dates & Vacations",
    metaDescription:
      "Complete Delhi University Academic Calendar 2026-27 breakdown. Track odd semester theory exams (Dec 4), autumn break, winter vacation, and practicals.",
    focusKeyword: "DU academic calendar 2026-27",
    secondaryKeywords: ["Delhi university first semester exam date", "DU winter vacation 2026", "DU semester 1 classes start date"],
    lsiKeywords: ["UGCF 2022 timeline", "66.6% attendance rule DU", "dispersal of classes"],
    keyTakeaways: [
      "The Autumn Vacation (mid-semester break) is scheduled from October 18 to October 25, 2026.",
      "Semester 1 Theory Examinations will commence on December 4, 2026.",
      "Classes are dispersed on November 20 to make way for mandatory practical exams and internal assessments.",
    ],
    faqs: [
      {
        q: "When do the first semester exams start in DU for the 2026-27 batch?",
        a: "The theory examinations for the first semester will commence on December 4, 2026.",
      },
      {
        q: "When is the winter vacation in Delhi University for 2026?",
        a: "The winter vacation is scheduled for a short window from December 25 to December 31, 2026.",
      },
    ],
  },

  "du-scholarships-fee-concession-2026": {
    seoTitle: "DU Scholarships & Fee Concession 2026: FSS, NSP & Aid Guide",
    metaDescription:
      "Every scholarship and fee concession available to Delhi University students in 2026. Covers FSS, NSP, college-level aid, SC/ST/OBC/EWS schemes, and step-by-step applications.",
    focusKeyword: "DU scholarship 2026",
    secondaryKeywords: ["Delhi University fee concession", "NSP scholarship Delhi University", "DU FSS scheme 2026"],
    lsiKeywords: ["Dean Students Welfare DSW", "income certificate SDM", "post matric scholarship SC ST"],
    keyTakeaways: [
      "DU FSS provides up to 100% fee waiver for students with family income under ₹4 Lakhs.",
      "The National Scholarship Portal (NSP) offers separate schemes for SC, ST, OBC, EWS, and minority students.",
      "You can often stack multiple scholarship sources — FSS, NSP, and college-level fee concessions simultaneously.",
    ],
    faqs: [
      {
        q: "How do I apply for DU Financial Support Scheme (FSS)?",
        a: "Apply online through the DSW portal (dsw.du.ac.in) after admissions conclude. You need an income certificate issued after April 1, 2026.",
      },
      {
        q: "Can I get both NSP scholarship and DU fee concession?",
        a: "In many cases, yes. You can apply for NSP and college-level fee concession simultaneously, though FSS and college concessions may overlap.",
      },
    ],
  },

  "du-first-year-book-list-2026": {
    seoTitle: "DU First Year Book List 2026: Buy, Skip & Free Resources",
    metaDescription:
      "Guide on buying books for your first year at Delhi University in 2026. Course-wise essential readings, cheapest bookshops, free e-resources, and what to skip.",
    focusKeyword: "DU first year book list 2026",
    secondaryKeywords: ["where to buy Delhi University books", "DU economics semester 1 books", "DU library e-resources"],
    lsiKeywords: ["Patel Chest bookshop", "Hal Varian Microeconomics", "DULS e-resources JSTOR"],
    keyTakeaways: [
      "Follow the 2-Week Rule: attend lectures for 2 weeks before buying any textbook.",
      "Only 2-3 books per semester are truly essential; the rest can be borrowed or xeroxed.",
      "DU Library System (DULS) provides free access to JSTOR, EBSCO, and the National Digital Library.",
    ],
    faqs: [
      {
        q: "Which books should I buy for DU Economics Honours Semester 1?",
        a: "The two essential purchases are Hal Varian's Microeconomics and Alpha C. Chiang's Mathematical Methods. Wait for professor guidance before buying anything else.",
      },
      {
        q: "Where is the cheapest place to buy DU books in North Campus?",
        a: "The Patel Chest / Maurice Nagar area offers the best prices for course-specific books and xerox copies.",
      },
    ],
  },

  "du-anti-ragging-rules-fresher-safety-2026": {
    seoTitle: "DU Anti-Ragging Rules 2026: Complaint Process & Safety Guide",
    metaDescription:
      "Critical safety guide for Delhi University freshers 2026. Anti-ragging complaint process, UGC helpline 1800-180-5522, mandatory undertakings, and campus safety protocols.",
    focusKeyword: "DU anti ragging complaint",
    secondaryKeywords: ["Delhi University ragging helpline number", "antiragging.in undertaking DU", "DU fresher safety 2026"],
    lsiKeywords: ["UGC anti ragging regulations", "Joint Control Room DU", "ragging punishment expulsion"],
    keyTakeaways: [
      "The 24x7 national anti-ragging helpline is 1800-180-5522 — calls can be made anonymously.",
      "Every student and parent must submit a mandatory anti-ragging undertaking at antiragging.in.",
      "Severe ragging (physical/sexual) leads to permanent expulsion, FIR, and a national database ban blocking admission to any Indian university.",
    ],
    faqs: [
      {
        q: "What is the anti-ragging helpline number for DU?",
        a: "The national anti-ragging helpline is 1800-180-5522, operational 24x7. You can also email helpline@antiragging.in.",
      },
      {
        q: "Is the anti-ragging undertaking mandatory for DU students?",
        a: "Yes, every student and their parent/guardian must submit the undertaking online at antiragging.in. Non-compliance can put your admission on hold.",
      },
    ],
  },

  "du-ncweb-admission-cutoff-schedule-2026": {
    seoTitle: "DU NCWEB Admission 2026: 2nd Cutoff Schedule & Marks Guide",
    metaDescription:
      "Guide on Delhi University NCWEB admissions 2026. Covers cutoff schedules, Class 12 merit calculation, center selection, fee structure, and NCWEB vs Regular.",
    focusKeyword: "DU NCWEB admission 2026",
    secondaryKeywords: ["Delhi University NCWEB cut off list 2026", "NCWEB 2nd cutoff release date", "NCWEB vs regular DU"],
    lsiKeywords: ["Best of Four calculation NCWEB", "Miranda House NCWEB center", "Delhi women residence proof"],
    keyTakeaways: [
      "NCWEB admissions require zero CUET score; selection is based purely on Class 12th board examination percentage.",
      "Classes take place exclusively on Saturdays or Sundays across 26 DU college centers.",
      "Under 2nd Cutoff (Aug 3), candidates apply online at ncwebadmission.uod.ac.in.",
    ],
    faqs: [
      {
        q: "Do I need CUET for DU NCWEB admission?",
        a: "No, NCWEB admissions are based entirely on Class 12th board marks for female candidates residing in Delhi.",
      },
      {
        q: "Can non-Delhi residents apply for DU NCWEB?",
        a: "No, NCWEB is strictly reserved for female candidates who have proof of residence in the NCT of Delhi.",
      },
    ],
  },

  "du-foreign-language-courses-admission-2026": {
    seoTitle: "DU Foreign Language Courses 2026: Certificate & Diploma Guide",
    metaDescription:
      "Delhi University foreign language courses 2026. Covers Certificate, Diploma, Advanced Diploma admissions at St. Stephen's, Hansraj, GRS department, and career scope.",
    focusKeyword: "DU foreign language courses 2026",
    secondaryKeywords: ["St Stephen's language courses admission 2026", "DU German language course fee", "Certificate in French DU"],
    lsiKeywords: ["CEFR language levels A1 B2", "DELF DELE Goethe Zertifikat", "GRS department entrance test"],
    keyTakeaways: [
      "DU offers part-time Certificate, Diploma, and Advanced Diploma courses in 12+ European and Asian languages.",
      "Classes at St. Stephen's and college centers run in early morning (7:30 AM) or evening (4:00 PM) batches.",
      "Fluency at B2 level opens corporate roles with starting salaries of ₹6-12 LPA and tuition-free European university admissions.",
    ],
    faqs: [
      {
        q: "Can students from other colleges join St. Stephen's foreign language courses?",
        a: "Yes, St. Stephen's Centre for Languages accepts external applicants as well as Stephenians for its part-time language programs.",
      },
      {
        q: "Are DU foreign language courses part-time or full-time?",
        a: "Most Certificate, Diploma, and Advanced Diploma courses are part-time (3 days a week), allowing you to pursue them alongside your regular degree.",
      },
    ],
  },

  "du-ba-economics-hons-vs-ba-programme-2026": {
    seoTitle: "DU BA Econ Hons vs BA Programme 2026: UGCF 4-Year Major Rules",
    metaDescription:
      "Analysis comparing B.A. (Hons) Economics and B.A. Programme with Economics under DU UGCF 2022. Credit rules for 4th-year Honours conversion, math rigor, and placements.",
    focusKeyword: "DU BA Economics Hons vs BA Programme",
    secondaryKeywords: ["UGCF 2022 4 year honours degree", "DU BA Programme economics major credits", "DSE MA economics eligibility BA Programme"],
    lsiKeywords: ["MME calculus rigor", "Econometrics intermediate", "Single Major Honours NEP 2020"],
    keyTakeaways: [
      "Under UGCF 2022, a BA Programme student who completes 80+ credits in Economics earns a B.A. (Hons) Economics degree in Year 4.",
      "BA (Hons) Economics mandates Class 12 Math and involves heavy multivariable calculus, whereas BA Programme focuses on graphical economic theory.",
      "Top corporate recruiters (MBB, Big 4) hire from both programs based on analytical aptitude and case studies.",
    ],
    faqs: [
      {
        q: "Can a BA Programme student get an Honours degree in Economics at DU?",
        a: "Yes! Under UGCF 2022 (NEP 2020), if a BA Programme student completes 80 credits in Economics over 4 years (8 semesters), they are awarded a B.A. (Hons) Economics degree.",
      },
      {
        q: "Is Class 12 Math compulsory for BA Programme with Economics?",
        a: "No, Class 12 Math is only compulsory for admission to B.A. (Hons) Economics. BA Programme with Economics does not mandate Class 12 Math.",
      },
    ],
  },

  "du-equal-opportunity-cell-disability-guidelines-2026": {
    seoTitle: "DU Equal Opportunity Cell (EOC) 2026: PwBD Quota & Scribe Guide",
    metaDescription:
      "Delhi University Equal Opportunity Cell (EOC) 2026 guide. Covers 5% PwBD reservation rules, scribe allocation, assistive technology labs, and grievance redressal.",
    focusKeyword: "DU Equal Opportunity Cell 2026",
    secondaryKeywords: ["DU PwBD reservation rules 2026", "DU scribe guidelines exam", "EOC Delhi University Arts Faculty"],
    lsiKeywords: ["UDID card mandatory DU", "compensatory time DU exam", "DAISY audiobooks Central Library"],
    keyTakeaways: [
      "Delhi University reserves 5% supernumerary seats across all courses for Persons with Benchmark Disabilities (PwBD).",
      "PwBD candidates using scribes receive 20 minutes compensatory extra time per hour of examination (total 4 hours for 3-hour paper).",
      "Valid UDID card issued by Government of India is mandatory for all PwBD fee waivers and scribe allocations.",
    ],
    faqs: [
      {
        q: "How much extra time do PwBD students get in DU semester exams?",
        a: "PwBD students utilizing a scribe or having benchmark disabilities receive 20 minutes of compensatory time per hour (1 extra hour for a 3-hour exam).",
      },
      {
        q: "Can PwBD students get a fee waiver at Delhi University?",
        a: "Yes, eligible PwBD candidates receive 100% waiver of tuition and exam fees, and 50% waiver on DU university hostel room rent.",
      },
    ],
  },

  "du-hostel-mess-food-menu-rules-2026": {
    seoTitle: "DU North Campus Hostel Mess & PG Food Guide 2026: Menus & Cost",
    metaDescription:
      "Food survival guide for Delhi University students in 2026. Covers North/South Campus hostel mess menus, monthly mess fees, PG tiffin services, food hubs, and nutrition.",
    focusKeyword: "DU hostel mess menu 2026",
    secondaryKeywords: ["DU hostel mess fees monthly", "North Campus PG tiffin service", "budget food spots DU Kamla Nagar"],
    lsiKeywords: ["mess rebate rules DU", "Hudson Lane student food", "Patel Chest canteen tea"],
    keyTakeaways: [
      "Monthly hostel mess bills at DU range between ₹3,200 and ₹4,800 under student mess committee management.",
      "Mess rebate is allowed for continuous absence of 3+ days with prior written notice to the warden.",
      "Hiring a shared cook for a 3-4 person PG is often healthier and cheaper than commercial tiffin services.",
    ],
    faqs: [
      {
        q: "How much does a DU hostel mess cost per month?",
        a: "On average, DU college and university hostel mess bills range between ₹3,200 and ₹4,800 per month.",
      },
      {
        q: "Can I get a rebate on my DU hostel mess bill if I go home?",
        a: "Yes, mess rebate is permitted if you submit a prior notice for continuous absence of 3 or more days.",
      },
    ],
  },

  "game-theory-nash-equilibrium-du-economics-guide": {
    seoTitle: "Game Theory & Nash Equilibrium: Matrix Derivations & Numericals",
    metaDescription:
      "Masterclass on Game Theory and Nash Equilibrium for DU Economics students. Covers dominant strategies, Prisoner's Dilemma, mixed strategies, and 3 exam numericals.",
    focusKeyword: "Game Theory Nash Equilibrium economics",
    secondaryKeywords: ["Prisoner's Dilemma payoff matrix", "Mixed Strategy Nash Equilibrium formula", "Cournot duopoly best response"],
    lsiKeywords: ["Strictly dominant strategy", "Indifference principle game theory", "Pure Strategy Nash Equilibrium"],
    keyTakeaways: [
      "A strategy is strictly dominant if it yields a strictly higher payoff regardless of opponent actions.",
      "At a Nash Equilibrium, every player is playing a mutual Best Response; no player has an incentive to deviate unilaterally.",
      "In a Mixed Strategy NE, each player chooses probabilities that make the opponent indifferent between their pure strategies.",
    ],
    faqs: [
      {
        q: "What is the difference between a Pure Strategy and a Mixed Strategy Nash Equilibrium?",
        a: "A Pure Strategy NE occurs when players choose deterministic actions, while a Mixed Strategy NE occurs when players randomize over pure strategies with specific probabilities.",
      },
      {
        q: "Why is the Prisoner's Dilemma equilibrium Pareto inefficient?",
        a: "Because both players defecting (1,1) is a Nash Equilibrium, even though mutual cooperation (3,3) would yield higher payoffs for both players.",
      },
    ],
  },

  "du-mid-entry-performance-based-admission-2026": {
    seoTitle: "DU Mid-Entry & Performance-Based Admission 2026: Full Guide",
    metaDescription:
      "Complete guide to Delhi University Mid-Entry registration (Aug 3-5) and Performance-Based UG allotment for 2026. Covers eligibility, correction window, and document verification.",
    focusKeyword: "DU Mid-Entry admission 2026",
    secondaryKeywords: ["DU performance based programs 2026", "CSAS Mid-Entry registration", "DU Round 3 allotment August"],
    lsiKeywords: ["B.El.Ed DU admission", "BMS performance test", "CSAS preference locking"],
    keyTakeaways: [
      "Mid-Entry registration opened August 3 and closes August 5, 2026 at 11:59 PM on the CSAS UG portal.",
      "Performance-Based programs (B.El.Ed, BMS, BBA FIA) release first allotment on August 6, using 50% CUET + 50% performance test weightage.",
      "Existing CSAS applicants can use the concurrent correction window to update scores, certificates, and preference order.",
    ],
    faqs: [
      {
        q: "Can I register for DU Mid-Entry if I missed the initial CSAS registration?",
        a: "Yes, Mid-Entry is specifically designed for new applicants who missed the initial registration window. Register at admission.uod.ac.in by August 5, 2026.",
      },
      {
        q: "What is the difference between Mid-Entry and Performance-Based admission at DU?",
        a: "Mid-Entry is a late registration window for CUET-based CSAS allocation. Performance-Based admission requires a separate college-conducted test (interview, trial, etc.) in addition to CUET scores.",
      },
    ],
  },

  "du-aditi-bhagini-offline-merit-admission-2026": {
    seoTitle: "DU Aditi & Bhagini Nivedita Offline Merit Admission 2026",
    metaDescription:
      "DU grants one-time offline merit-based admission to Aditi Mahavidyalaya and Bhagini Nivedita College for 2026. No CUET required. Complete eligibility, dates, and application process.",
    focusKeyword: "Aditi Mahavidyalaya offline admission 2026",
    secondaryKeywords: ["Bhagini Nivedita College merit admission", "DU offline admission without CUET", "DU vacant seats August 2026"],
    lsiKeywords: ["Class 12 merit based DU", "Bawana college admission", "Najafgarh DU college"],
    keyTakeaways: [
      "DU has granted one-time special permission for offline merit-based admission at Aditi Mahavidyalaya (Bawana) and Bhagini Nivedita College (Najafgarh).",
      "CUET UG 2026 score is NOT required — admission is based on Class 12 aggregate percentage (best of 4 subjects).",
      "Applications must be submitted directly to the college by August 5, with document verification on August 6 and fee payment by August 7.",
    ],
    faqs: [
      {
        q: "Do I need CUET to apply for Aditi Mahavidyalaya offline admission?",
        a: "No. This special offline process uses Class 12 board marks as the primary selection metric. CUET score is not required.",
      },
      {
        q: "Is the degree from Aditi Mahavidyalaya the same as other DU colleges?",
        a: "Yes, the degree awarded is a full University of Delhi degree, identical in recognition to any other DU constituent college.",
      },
    ],
  },

  "consumer-theory-indifference-curves-utility-maximization": {
    seoTitle: "Consumer Theory & Indifference Curves: Derivations & Numericals",
    metaDescription:
      "Comprehensive masterclass on Consumer Theory for DU Economics. Covers utility functions, indifference curves, MRS derivation, Lagrangian optimization, and 3 worked numerical problems.",
    focusKeyword: "Consumer Theory indifference curves economics",
    secondaryKeywords: ["MRS derivation utility function", "Lagrangian utility maximization", "Slutsky income substitution effect"],
    lsiKeywords: ["Cobb-Douglas demand function", "budget constraint tangency", "Marshallian demand"],
    keyTakeaways: [
      "At the consumer's optimum, MRS = MU1/MU2 = p1/p2 (the tangency condition between indifference curve and budget line).",
      "For Cobb-Douglas utility U = x1^a * x2^b, the income share spent on Good 1 is always a/(a+b) regardless of prices.",
      "Slutsky decomposition breaks the total price effect into a Substitution Effect (along the compensated budget line) and an Income Effect.",
    ],
    faqs: [
      {
        q: "Why can two indifference curves never intersect?",
        a: "If two ICs crossed at point P, transitivity would be violated: bundles on different ICs (with different utility levels) would both be indifferent to P, implying they have equal utility — a contradiction.",
      },
      {
        q: "What is the expenditure share shortcut for Cobb-Douglas utility?",
        a: "For U = x1^a * x2^b, the consumer always spends fraction a/(a+b) of income on Good 1 and b/(a+b) on Good 2, regardless of prices.",
      },
    ],
  },

  "solow-growth-model-golden-rule-du-economics": {
    seoTitle: "Solow Growth Model: Steady State & Golden Rule Derivation",
    metaDescription:
      "Masterclass on the Solow Growth Model for DU Economics. Covers steady state k*, Golden Rule level k*gold, technological progress, and 3 solved exam numericals.",
    focusKeyword: "Solow Growth Model Golden Rule",
    secondaryKeywords: ["Solow steady state derivation", "Golden Rule savings rate s_gold", "macroeconomics growth numericals"],
    lsiKeywords: ["capital labor ratio k*", "break even investment line", "effective labor A*L"],
    keyTakeaways: [
      "The fundamental Solow differential equation is dk/dt = s*f(k) - (n+delta)*k.",
      "At the Golden Rule steady state, MPK = n + delta (or n + g + delta when technological progress is present).",
      "For a Cobb-Douglas production function, the Golden Rule savings rate equals the capital share of income (alpha).",
    ],
    faqs: [
      {
        q: "What is the Golden Rule of Capital in the Solow Model?",
        a: "The Golden Rule is the steady-state capital-labor ratio k*gold that maximizes steady-state consumption per worker. It occurs where MPK = n + delta.",
      },
      {
        q: "What happens to steady state capital when the savings rate increases?",
        a: "An increase in savings rate s shifts the actual investment curve upward, increasing steady-state capital per worker k* and output per worker y*.",
      },
    ],
  },

  "du-internship-placement-cell-guide-2026": {
    seoTitle: "DU Internship & Placement Cell Guide 2026: Resumes & Drives",
    metaDescription:
      "DU placement cell masterclass for 2026. Covers Central Placement Cell (CPC) registration, college placement drives, 1-page CV formatting, case interview prep, and corporate recruitment.",
    focusKeyword: "DU Placement Cell guide 2026",
    secondaryKeywords: ["DU Central Placement Cell registration", "SRCC placement cell resume format", "guesstimate frameworks consulting interview"],
    lsiKeywords: ["XYZ resume bullet formula", "Big 4 recruitment DU", "winter internship fair DU"],
    keyTakeaways: [
      "DU operates both the university-wide Central Placement Cell (CPC) at cpc.du.ac.in and autonomous college placement cells.",
      "Corporate CVs must follow a strict 1-page format with XYZ action-impact bullet points for work experience.",
      "Tier-1 consulting and finance recruitment occurs during Phase 1 (August–October) of the academic calendar.",
    ],
    faqs: [
      {
        q: "How do I register for DU Central Placement Cell (CPC)?",
        a: "Register online at cpc.du.ac.in with your DU institutional email ID, college ID card scan, and verified 1-page resume.",
      },
      {
        q: "Can off-campus college students participate in North Campus placement drives?",
        a: "No, college-level placement drives are restricted to enrolled students. However, ALL DU students can register for CPC drives at cpc.du.ac.in.",
      },
    ],
  },

  "du-migration-college-transfer-rules-2026": {
    seoTitle: "DU Inter-College Migration Rules 2026: Eligibility & NOC",
    metaDescription:
      "Guide to Delhi University Inter-College Migration for 2026. Covers 3rd semester transfer rules under UGCF 2022, NOC formats, CGPA eligibility, and application steps.",
    focusKeyword: "DU inter college migration 2026",
    secondaryKeywords: ["DU college transfer rules 3rd semester", "NOC for migration DU format", "DU college migration CGPA cutoff"],
    lsiKeywords: ["DU Ordinance XII Clause 4", "vacant seats 3rd semester DU", "inter university migration DU"],
    keyTakeaways: [
      "Inter-college migration is permitted ONLY at the start of Semester 3 (Year 2) within the exact same degree program.",
      "Migration requires seat vacancy in the target college and dual NOCs from both releasing and receiving college Principals.",
      "Sports/ECA quota students and SOL/NCWEB students are prohibited from inter-college migration to regular courses.",
    ],
    faqs: [
      {
        q: "When can I apply for inter-college migration at Delhi University?",
        a: "Migration is allowed strictly at the beginning of Semester III (Year 2) after Semester I and II results are declared.",
      },
      {
        q: "Can I change my course during inter-college migration?",
        a: "No. Migration is permitted only within the exact same degree course (e.g. B.Com Hons to B.Com Hons).",
      },
    ],
  },

  "du-fresher-orientation-induction-week-2026": {
    seoTitle: "DU Fresher Orientation & Induction Week 2026: Complete Guide",
    metaDescription:
      "Complete guide to DU fresher orientation week 2026-27. Covers induction schedule, Day 1 checklist, SAMARTH portal setup, library registration, and anti-ragging affidavit.",
    focusKeyword: "DU fresher orientation 2026",
    secondaryKeywords: ["DU induction week schedule", "SAMARTH portal student login", "DU library card registration"],
    lsiKeywords: ["UGC Student Induction Programme", "N-LIST e-resource access", "anti-ragging affidavit DU"],
    keyTakeaways: [
      "DU 2026-27 academic session commenced July 28; college-level orientation runs through early August.",
      "First-time SAMARTH login requires enrollment number, mobile OTP verification, and digital photo upload.",
      "GE/SEC/VAC course registration window opens during the first 2 weeks — popular subjects fill up within hours.",
    ],
    faqs: [
      {
        q: "What documents should I carry on DU orientation Day 1?",
        a: "Carry CSAS allotment letter, fee receipt, Class 10 & 12 marksheets (originals + photocopies), CUET scorecard, migration certificate, 6 passport photos, and Aadhaar card.",
      },
      {
        q: "How do I register for the DU Central Library?",
        a: "Submit 2 passport photos and your enrollment number to the college librarian during orientation week. Economics and PG students should also register at the Central Library, Arts Faculty.",
      },
    ],
  },

  "oligopoly-cournot-bertrand-stackelberg-du-economics": {
    seoTitle: "Oligopoly: Cournot, Bertrand & Stackelberg Derivations",
    metaDescription:
      "Masterclass on Oligopoly Models for DU Economics. Complete derivations of Cournot quantity competition, Bertrand price paradox, Stackelberg leader-follower, and 3 exam numericals.",
    focusKeyword: "Cournot Bertrand Stackelberg oligopoly",
    secondaryKeywords: ["Cournot Nash equilibrium derivation", "Bertrand paradox homogeneous goods", "Stackelberg first mover advantage"],
    lsiKeywords: ["reaction function best response", "N-firm Cournot generalization", "backward induction sequential game"],
    keyTakeaways: [
      "In Cournot duopoly with symmetric firms, each firm produces (a-c)/(3b) and market price is (a+2c)/3.",
      "The Bertrand Paradox: with homogeneous products and equal costs, just 2 firms drive price to marginal cost.",
      "Stackelberg leader produces (a-c)/(2b) — exactly the monopoly quantity — exploiting first-mover advantage.",
    ],
    faqs: [
      {
        q: "Why is the Bertrand outcome called a paradox?",
        a: "Because with just 2 firms, price competition drives the equilibrium to P = MC with zero economic profits — the same as perfect competition with infinitely many firms.",
      },
      {
        q: "Does the Stackelberg leader always earn more than the follower?",
        a: "Yes, with linear demand and identical costs, the leader's first-mover commitment to higher output forces the follower to produce less, giving the leader higher profit.",
      },
    ],
  },

  "du-societies-clubs-how-to-join-2026": {
    seoTitle: "DU Societies & Clubs 2026: How to Join & Audition Tips",
    metaDescription:
      "Guide to joining DU college societies and clubs for 2026-27. Covers audition processes, top societies across debate, drama, economics, and music, plus extracurricular portfolio tips.",
    focusKeyword: "DU college societies how to join 2026",
    secondaryKeywords: ["DU debate society audition tips", "SRCC Finance Investment Cell recruitment", "DU dramatics society audition"],
    lsiKeywords: ["society recruitment August DU", "extracurricular portfolio MBA", "2-society rule freshers"],
    keyTakeaways: [
      "Society recruitment runs August through mid-September via auditions, written tests, and panel interviews.",
      "Follow the 2-Society Rule in Year 1: one academic society + one passion-based society to avoid burnout.",
      "Document all society achievements in a running CV log for placement season and MBA applications.",
    ],
    faqs: [
      {
        q: "How many societies should a DU fresher join?",
        a: "We recommend a maximum of 2 active societies in Year 1: one aligned with your academic interests and one passion-based. You can add more in Year 2.",
      },
      {
        q: "When do DU college societies recruit freshers?",
        a: "Most societies recruit during August-September, starting with orientation week stalls, followed by auditions/tests in weeks 3-4, and final interviews in weeks 5-6.",
      },
    ],
  },

  "is-lm-model-derivation-policy-effectiveness-du": {
    seoTitle: "IS-LM Model: Goods & Money Market Derivation & Numericals",
    metaDescription:
      "Masterclass on the IS-LM Model for DU Economics. Covers IS and LM algebraic derivations, general equilibrium, fiscal/monetary policy multipliers, crowding out, and 3 solved exam numericals.",
    focusKeyword: "IS-LM model derivation economics",
    secondaryKeywords: ["IS LM general equilibrium Y* r*", "fiscal policy multiplier crowding out", "liquidity trap classical case IS-LM"],
    lsiKeywords: ["Keynesian liquidity preference", "marginal propensity to consume c", "investment interest sensitivity d"],
    keyTakeaways: [
      "The IS curve represents goods market equilibrium (Y = C + I + G); downward sloping as higher interest rates reduce investment.",
      "The LM curve represents money market equilibrium (M/P = L); upward sloping as higher income increases transaction money demand.",
      "Fiscal policy is less effective in IS-LM than simple Keynesian multiplier due to crowding out of private investment.",
    ],
    faqs: [
      {
        q: "What is the crowding-out effect in the IS-LM model?",
        a: "When government spending increases, IS shifts right, raising income but also pushing up interest rates. Higher interest rates reduce private investment, partially offsetting the income expansion.",
      },
      {
        q: "What happens to policy effectiveness in a Liquidity Trap?",
        a: "In a Liquidity Trap (horizontal LM curve), fiscal policy has maximal effectiveness (zero crowding out), while monetary policy is completely ineffective.",
      },
    ],
  },

  "du-internal-assessment-ia-strategy-2026": {
    seoTitle: "DU Internal Assessment (IA) Masterclass 2026: Scoring 30/30",
    metaDescription:
      "Tactical guide to scoring 100% in Internal Assessment (IA) at Delhi University under UGCF 2022. Covers 30-40% IA weightage, class tests, assignment templates, presentation tips, and IAMC audit.",
    focusKeyword: "DU Internal Assessment IA strategy 2026",
    secondaryKeywords: ["UGCF 2022 IA 30 marks breakdown", "DU assignment cover page template", "IAMC portal grievance DU"],
    lsiKeywords: ["attendance slab marks DU", "continuous assessment 40 marks", "class test mid sem DU"],
    keyTakeaways: [
      "Internal Assessment carries 30 to 40 marks in every paper under UGCF 2022 and serves as the highest-ROI GPA booster.",
      "The 30-mark IA block is split into 12 marks Class Test, 12 marks Assignment/Presentation, and 6 marks Attendance.",
      "Always audit your draft IA scores on the college IAMC portal 2 weeks before semester exams to correct errors.",
    ],
    faqs: [
      {
        q: "How are attendance marks calculated in DU Internal Assessment?",
        a: "Attendance is scored on a 6-mark scale: 85%+ gets 6 marks, 80-85% gets 5, 75-80% gets 4, 70-75% gets 3, 66.67-70% gets 2, and below 66.67% gets 0 marks.",
      },
      {
        q: "Can I challenge my Internal Assessment marks if there is an error?",
        a: "Yes. Every college has an Internal Assessment Monitoring Committee (IAMC). Submit a formal IA Grievance Form within 48 hours of draft mark uploading.",
      },
    ],
  },

  "du-library-system-opac-e-resources-guide-2026": {
    seoTitle: "DU Library System 2026: OPAC, N-LIST & JSTOR Remote Access",
    metaDescription:
      "Guide to Delhi University's library network in 2026. Covers Central Library, Ratan Tata Library (DSE), OPAC catalogue search, INFLIBNET N-LIST, JSTOR remote VPN access, and borrowing rules.",
    focusKeyword: "DU library system OPAC e-resources 2026",
    secondaryKeywords: ["Ratan Tata Library DSE access", "INFLIBNET N-LIST student registration DU", "JSTOR remote access DU VPN"],
    lsiKeywords: ["Central Library Arts Faculty North Campus", "OPAC book call number search", "DU library borrowing rules UG PG"],
    keyTakeaways: [
      "Delhi University Library System (DULS) includes 34+ unit libraries including Central Library, Ratan Tata Library (DSE), and Central Science Library.",
      "INFLIBNET N-LIST provides free access to 6,000+ e-journals and 1,99,500+ e-books using your DU email ID.",
      "Borrowing limits: UG students can borrow 3-4 books for 14 days; books can be renewed online via opac.du.ac.in.",
    ],
    faqs: [
      {
        q: "Can undergraduate students access the Ratan Tata Library (RTL) at DSE?",
        a: "Yes. DU Economics Honours and PG students can access RTL reading rooms by obtaining a student reader pass from the RTL desk.",
      },
      {
        q: "How do I access JSTOR from home as a DU student?",
        a: "Log into the DULS Remote Access Portal (csl.du.ac.in / Knimbus) using your DULS library credentials to authenticate your home browser.",
      },
    ],
  },

  "monopoly-price-discrimination-deadweight-loss-du": {
    seoTitle: "Monopoly & Price Discrimination: 1st, 2nd, 3rd Degree & DWL",
    metaDescription:
      "Masterclass on Monopoly and Price Discrimination for DU Economics. Covers MR=MC equilibrium, Deadweight Loss derivation, 1st, 2nd, and 3rd degree models, and 3 solved exam numericals.",
    focusKeyword: "Monopoly price discrimination deadweight loss",
    secondaryKeywords: ["MR MC monopoly equilibrium", "Lerner Index of monopoly power formula", "3rd degree price discrimination numerical"],
    lsiKeywords: ["allocative inefficiency monopoly", "inverse elasticity pricing rule", "consumer surplus extraction"],
    keyTakeaways: [
      "Monopoly profit maximization occurs where MR = MC, resulting in P > MC and creating Deadweight Loss.",
      "1st-degree price discrimination extracts 100% consumer surplus and eliminates Deadweight Loss (Pareto efficient output).",
      "3rd-degree price discrimination charges a higher price in the market with lower price elasticity of demand (|e1| < |e2| => P1 > P2).",
    ],
    faqs: [
      {
        q: "Why is a monopoly inefficient compared to perfect competition?",
        a: "A monopoly restricts output to Q_m < Q_pc and charges P_m > MC, creating a Deadweight Loss (DWL) triangle where total economic surplus is reduced.",
      },
      {
        q: "What is the Inverse Elasticity Pricing Rule in third-degree price discrimination?",
        a: "It states that a firm charges a higher price in the market segment with less elastic demand, using P1/P2 = (1 - 1/|e2|) / (1 - 1/|e1|).",
      },
    ],
  },

  "du-sports-quota-trials-certificate-marking-2026": {
    seoTitle: "DU Sports Quota Admission 2026: Trials & Certificate Marking",
    metaDescription:
      "Guide to DU Sports Quota Admission 2026-27. Covers 5% supernumerary seats, 50% certificate + 25% trial + 25% CUET formula, trial ground venues, and tie-breaker rules.",
    focusKeyword: "DU sports quota admission 2026",
    secondaryKeywords: ["DU sports trial schedule 2026", "DU sports certificate marking scheme", "DU sports quota merit calculator"],
    lsiKeywords: ["Category B C D sports trials", "DUSC North Campus polo ground", "supernumerary 5 percent DU"],
    keyTakeaways: [
      "DU Sports Quota uses a 100-mark formula: 50% Certificate Evaluation + 25% Physical Trial + 25% CUET score.",
      "International athletes (Category A) receive direct admission without appearing for sports trials.",
      "Sports trials require a minimum 40% (10 out of 25 marks) to qualify for final seat allocation.",
    ],
    faqs: [
      {
        q: "How are Sports Quota marks calculated in DU?",
        a: "Merit is calculated out of 100 marks: 50 marks for your highest sports certificate, 25 marks for the physical trial, and 25 marks for your normalized CUET score.",
      },
      {
        q: "Do I need to appear for sports trials if I played in National Games?",
        a: "Yes, Category B, C, and D national/state players must appear for trials. Only international representatives (Category A) get direct admission.",
      },
    ],
  },

  "du-cluster-innovation-centre-cic-btech-2026": {
    seoTitle: "DU Cluster Innovation Centre (CIC) 2026: B.Tech IT & MI Guide",
    metaDescription:
      "Guide to Cluster Innovation Centre (CIC), Delhi University for 2026. Covers B.Tech IT & Mathematical Innovation, CUET combination, Meta-College model, and 18 LPA placement packages.",
    focusKeyword: "DU Cluster Innovation Centre CIC BTech 2026",
    secondaryKeywords: ["BTech IT Mathematical Innovation DU eligibility", "CIC DU average package placement", "CUET combination BTech CIC DU"],
    lsiKeywords: ["Meta College project learning DU", "CIC DU seat matrix 50 seats", "Math mandatory BTech CIC"],
    keyTakeaways: [
      "CIC DU offers B.Tech in IT & Mathematical Innovation and BA (Hons) Humanities with 50% weightage to hands-on project work.",
      "CUET combination for B.Tech: Language + Mathematics / Applied Mathematics + General Test (Math mandatory).",
      "Average placement package is ₹15.8 LPA with top offers reaching ₹38 LPA in SDE and Quant roles.",
    ],
    faqs: [
      {
        q: "Is Class 12 Math mandatory for B.Tech at CIC Delhi University?",
        a: "Yes. Class 12 Mathematics (or Applied Mathematics) is strictly mandatory for B.Tech (IT & Mathematical Innovation) at CIC.",
      },
      {
        q: "What makes Cluster Innovation Centre (CIC) different from traditional engineering colleges?",
        a: "CIC focuses on hands-on project-based learning with a Meta-College concept allowing students to take electives across DU colleges, rather than standard lecture exams.",
      },
    ],
  },

  "introductory-econometrics-ols-assumptions-proofs-du": {
    seoTitle: "Introductory Econometrics & OLS Regression: Gauss-Markov Proofs",
    metaDescription:
      "Masterclass on Introductory Econometrics and OLS Regression for DU Economics. Covers OLS derivations, Gauss-Markov BLUE proof, R-squared ANOVA decomposition, and 3 exam numericals.",
    focusKeyword: "Introductory Econometrics OLS proofs Gauss-Markov",
    secondaryKeywords: ["Gauss Markov theorem BLUE proof econometrics", "OLS normal equations derivation", "R squared TSS ESS RSS decomposition"],
    lsiKeywords: ["zero conditional mean exogeneity", "homoskedasticity variance error", "t test F test econometrics regression"],
    keyTakeaways: [
      "OLS minimizes the Residual Sum of Squares (RSS) to obtain beta_1_hat = Cov(X,Y)/Var(X) and beta_0_hat = Y_bar - beta_1_hat*X_bar.",
      "The Gauss-Markov Theorem proves that OLS estimators are Best Linear Unbiased Estimators (BLUE) with minimum sampling variance.",
      "Total variation decomposes into TSS = ESS + RSS; R^2 measures the proportion of variance explained by the fitted regression line.",
    ],
    faqs: [
      {
        q: "What does BLUE stand for in the Gauss-Markov Theorem?",
        a: "BLUE stands for Best Linear Unbiased Estimator. 'Best' means having the minimum sampling variance among all linear, unbiased estimators.",
      },
      {
        q: "Why is the degrees of freedom for residual variance (n - 2) in simple linear regression?",
        a: "Because two degrees of freedom are lost in estimating the two sample parameters: intercept beta_0 and slope beta_1.",
      },
    ],
  },

  "consulting-case-interview-playbook-du-undergrads": {
    seoTitle: "Undergraduate Consulting Casebook: Frameworks & Guesstimates",
    metaDescription:
      "Consulting interview preparation playbook for DU undergraduates targeting MBB (McKinsey, BCG, Bain). Covers profitability trees, live case transcripts, Indian guesstimate data, and exhibit reading.",
    focusKeyword: "Consulting case interview playbook undergraduate",
    secondaryKeywords: ["McKinsey case interview framework DU", "profitability issue tree guesstimates", "market sizing cheat sheet India 2026"],
    lsiKeywords: ["MECE issue tree problem solving", "STAR framework consulting fit", "fast charging EV fleet case transcript"],
    keyTakeaways: [
      "Consulting case interviews test structured problem-solving, MECE issue tree logic, mental math, and executive presence.",
      "The 4 core frameworks include Profitability (Revenue/Cost drivers), Market Entry, Pricing Strategy, and M&A due diligence.",
      "Memorize baseline Indian demographics (1.44B population, 32 crore households, 35% urban split) for rapid guesstimate calculations.",
    ],
    faqs: [
      {
        q: "What is the MECE principle in consulting case interviews?",
        a: "MECE stands for Mutually Exclusive, Collectively Exhaustive. It ensures your problem breakdown covers all possibilities without any overlapping categories.",
      },
      {
        q: "How many cases should a DU student practice before campus placement drives?",
        a: "Top candidates typically practice 20 to 30 live mock cases with a casing partner, supplemented by 10 to 15 standalone market sizing guesstimates.",
      },
    ],
  },

  "isi-msqe-vs-dse-ma-economics-complete-guide": {
    seoTitle: "ISI MSQE vs DSE MA Economics: Cutoffs, Placements & ROI",
    metaDescription:
      "Comprehensive comparison between ISI MSQE (Delhi/Kolkata) and DSE MA Economics. Covers entrance exams (PEA/PEB vs CUET PG), mathematical intensity, ₹8,000/mo stipend, and quant placements.",
    focusKeyword: "ISI MSQE vs DSE MA Economics",
    secondaryKeywords: ["ISI MSQE entrance exam pattern PEA PEB", "DSE MA Economics CUET PG cutoff score", "quant finance placements ISI MSQE Tower Research"],
    lsiKeywords: ["measure theory real analysis economics", "Mas-Colell microeconomics DSE", "fully funded MA economics India"],
    keyTakeaways: [
      "ISI MSQE is 100% tuition-free and provides a universal ₹8,000/month stipend to all admitted students.",
      "ISI features ultra-small batch sizes (25-30 students) with extreme mathematical rigor (real analysis, measure theory, quant finance dominance).",
      "DSE features a large, vibrant batch (300 students) with unparalleled corporate consulting (MBB) and public policy placement networks.",
    ],
    faqs: [
      {
        q: "Does ISI MSQE have any tuition fee?",
        a: "No. ISI MSQE is completely tuition-free for all students and pays a monthly stipend of ₹8,000 plus an annual contingency grant of ₹8,000.",
      },
      {
        q: "Which program is better for quantitative finance and algorithmic trading?",
        a: "ISI MSQE is the undisputed leader for quantitative finance, HFTs, and hedge funds (Tower Research, Jane Street, WorldQuant, DE Shaw) due to its deep statistical foundation.",
      },
    ],
  },
  "microeconomics-formulas-theorems-cheat-sheet": {
    seoTitle: "Microeconomics Formula Sheet & Key Theorems: Complete DU Reference Guide 2026",
    metaDescription: "Complete microeconomics formula cheat sheet covering Consumer Theory, Producer Theory, Market Structures, Game Theory & Welfare Economics. Every formula DU Economics students need with worked examples.",
    focusKeyword: "microeconomics formula sheet",
    secondaryKeywords: ["microeconomics cheat sheet", "micro formulas DU", "slutsky equation formula", "MRS formula economics", "consumer theory formulas"],
    lsiKeywords: ["indifference curve", "budget constraint", "marginal rate of substitution", "elasticity of demand", "Nash equilibrium", "Pareto efficiency"],
    keyTakeaways: [
      "Every essential microeconomics formula organized by topic with definitions and exam tips",
      "Consumer Theory: MRS, Slutsky Equation, Roy's Identity, Shephard's Lemma with derivation hints",
      "Market Structures: Monopoly, Cournot, Bertrand, Stackelberg equilibrium conditions",
      "Game Theory: Nash Equilibrium, Mixed Strategy, Dominant Strategy formulas",
      "Welfare Economics: Pareto Efficiency, Social Welfare Function conditions"
    ],
    faqs: [
      {
        q: "What are the most important microeconomics formulas for DU exams?",
        a: "The most important formulas include MRS = MUx/MUy = Px/Py for consumer equilibrium, the Slutsky Equation for substitution and income effects, MR = MC for profit maximization across all market structures, and Nash Equilibrium conditions for game theory problems.",
      },
      {
        q: "How do I use the Slutsky Equation in numerical problems?",
        a: "The Slutsky Equation decomposes total price effect into substitution effect (always negative) and income effect. For normal goods both effects reinforce each other; for Giffen goods the positive income effect dominates the negative substitution effect.",
      },
    ],
  },
  "macroeconomics-formulas-models-quick-reference": {
    seoTitle: "Macroeconomics Formula Sheet: IS-LM, Solow, Phillips Curve & Multipliers 2026",
    metaDescription: "Complete macroeconomics formula reference covering IS-LM model, Solow Growth, AD-AS, Phillips Curve, Mundell-Fleming & policy multipliers. Essential for DU Economics semester exams.",
    focusKeyword: "macroeconomics formula sheet",
    secondaryKeywords: ["macro formulas cheat sheet", "IS-LM formula", "Solow model formula", "Keynesian multiplier formula", "Phillips curve equation"],
    lsiKeywords: ["aggregate demand", "money supply", "fiscal policy", "monetary policy", "GDP identity", "Taylor Rule"],
    keyTakeaways: [
      "Complete IS-LM equilibrium equations with fiscal and monetary policy multipliers",
      "Solow Growth Model: steady state, golden rule, and convergence formulas",
      "AD-AS model with short-run and long-run aggregate supply conditions",
      "Phillips Curve: original, expectations-augmented, and NAIRU formulations",
      "Mundell-Fleming model equations for fixed and floating exchange rate regimes"
    ],
    faqs: [
      {
        q: "What is the government expenditure multiplier formula?",
        a: "The government expenditure multiplier in a simple Keynesian model is 1/(1-MPC), where MPC is the marginal propensity to consume. In an open economy with taxes, it becomes 1/(1-MPC(1-t)+MPM) where t is the tax rate and MPM is marginal propensity to import.",
      },
      {
        q: "How do I derive the IS curve equation?",
        a: "The IS curve is derived from goods market equilibrium Y = C + I + G + NX. Substitute the consumption function C = C0 + c(Y-T), investment function I = I0 - br, and solve for Y in terms of r to get the downward-sloping IS curve.",
      },
    ],
  },
  "du-economics-semester-wise-syllabus-reading-list-2026": {
    seoTitle: "DU B.A. (Hons) Economics Complete Syllabus & Reading List 2026-27 (All 8 Semesters)",
    metaDescription: "Complete semester-wise syllabus for DU B.A. (Hons) Economics under UGCF 2022 NEP. All Core, DSE, GE, SEC, VAC papers with prescribed textbooks, credit weightage & exam pattern.",
    focusKeyword: "DU economics syllabus 2026",
    secondaryKeywords: ["DU BA Hons Economics syllabus", "Delhi University economics reading list", "DU economics course structure NEP", "UGCF 2022 economics syllabus"],
    lsiKeywords: ["semester wise papers", "prescribed textbooks", "credit weightage", "DSE papers", "GE electives", "internal assessment"],
    keyTakeaways: [
      "Full 8-semester course structure under UGCF 2022 with UPC codes and credit weightage",
      "Prescribed textbooks with author, edition, and relevant chapters for each paper",
      "DSE specialization options: Mathematical Economics, Econometrics, Public Policy, Finance",
      "GE, SEC, VAC, and AEC paper options with scoring patterns",
      "Exam pattern breakdown: theory marks vs internal assessment for each paper type"
    ],
    faqs: [
      {
        q: "How many credits does DU B.A. (Hons) Economics require for graduation?",
        a: "Under UGCF 2022 (NEP framework), DU B.A. (Hons) Economics requires a total of 176 credits across 8 semesters for the 4-year honours degree, or 132 credits for the 3-year regular degree.",
      },
      {
        q: "Which textbooks are prescribed for Intermediate Microeconomics at DU?",
        a: "The primary textbook is Hal Varian's Intermediate Microeconomics: A Modern Approach (9th edition). Supplementary readings include Pindyck & Rubinfeld and Snyder & Nicholson for additional problem sets.",
      },
    ],
  },
  "statistics-for-economics-probability-distributions-notes": {
    seoTitle: "Statistics for Economics Notes: Probability Distributions, Hypothesis Testing & Regression",
    metaDescription: "Complete statistics for economics study notes covering probability distributions (Normal, Binomial, Poisson), hypothesis testing, confidence intervals & OLS regression with worked numerical examples.",
    focusKeyword: "statistics for economics notes",
    secondaryKeywords: ["probability distributions economics", "hypothesis testing notes", "OLS regression notes", "statistics cheat sheet economics", "Bayes theorem economics"],
    lsiKeywords: ["normal distribution", "chi-square test", "t-distribution", "confidence interval", "p-value", "R-squared", "standard deviation"],
    keyTakeaways: [
      "Descriptive statistics: mean, median, mode, variance, standard deviation, skewness, kurtosis formulas",
      "Probability distributions: Binomial, Poisson, Normal, Chi-Square, t-distribution with worked examples",
      "Hypothesis testing: Type I/II errors, p-values, confidence intervals step-by-step",
      "Linear regression: OLS assumptions, coefficient interpretation, R-squared, F-test",
      "Bayes' Theorem with economic applications and worked numerical problems"
    ],
    faqs: [
      {
        q: "What is the difference between Type I and Type II errors?",
        a: "Type I error (alpha) is rejecting a true null hypothesis (false positive). Type II error (beta) is failing to reject a false null hypothesis (false negative). In economics, Type I error means concluding a policy has an effect when it does not.",
      },
      {
        q: "When should I use a t-test vs a z-test in economics?",
        a: "Use a z-test when the population variance is known and sample size is large (n > 30). Use a t-test when the population variance is unknown and you estimate it from the sample, especially with small samples (n < 30).",
      },
    ],
  },
  "indian-economy-key-data-statistics-cheat-sheet-2026": {
    seoTitle: "Indian Economy Key Data & Statistics 2026: GDP, Inflation, Trade & Budget Cheat Sheet",
    metaDescription: "Quick-reference cheat sheet with latest Indian economy data: GDP growth, CPI/WPI inflation, RBI rates, fiscal deficit, forex reserves, FDI/FII flows, unemployment & Union Budget 2026-27 allocations.",
    focusKeyword: "Indian economy data 2026",
    secondaryKeywords: ["India GDP growth rate 2026", "RBI monetary policy rates", "Indian economy statistics", "India fiscal deficit 2026", "Union Budget 2026-27 key data"],
    lsiKeywords: ["CPI inflation", "repo rate", "current account deficit", "foreign exchange reserves", "PLFS unemployment", "PLI scheme"],
    keyTakeaways: [
      "Latest GDP growth rate, quarterly and annual, with sectoral composition breakdown",
      "RBI monetary policy rates: Repo, Reverse Repo, CRR, SLR, MSF with recent changes",
      "Fiscal deficit trends (absolute and % of GDP) with FRBM targets",
      "Foreign trade data: exports, imports, current account balance, forex reserves",
      "Union Budget 2026-27 key allocations across defense, education, health, infrastructure"
    ],
    faqs: [
      {
        q: "What is India's current GDP growth rate in 2026?",
        a: "India's real GDP growth rate for FY 2025-26 is estimated at approximately 6.5-7.0% by the RBI and IMF, making it the fastest-growing major economy globally. The advance estimate by MoSPI provides the first official projection in January each year.",
      },
      {
        q: "What are the current RBI monetary policy rates?",
        a: "As of 2026, the key RBI rates are: Repo Rate around 6.00-6.25%, Standing Deposit Facility (SDF) rate 25 bps below Repo, Marginal Standing Facility (MSF) rate 25 bps above Repo, CRR at 4.0%, and SLR at 18.0%. Check the latest RBI Monetary Policy Statement for exact current rates.",
      },
    ],
  },
  "mathematical-economics-optimization-lagrangian-notes": {
    seoTitle: "Mathematical Economics Notes: Optimization, Lagrangian Multipliers & Constrained Maximization",
    metaDescription: "Complete mathematical economics study notes covering unconstrained & constrained optimization, Lagrangian method, Bordered Hessian, Kuhn-Tucker conditions, Envelope Theorem with 5+ worked problems.",
    focusKeyword: "mathematical economics optimization notes",
    secondaryKeywords: ["Lagrangian multiplier economics", "constrained optimization notes", "bordered Hessian economics", "Kuhn-Tucker conditions", "MME notes DU"],
    lsiKeywords: ["first order conditions", "second order conditions", "Hessian matrix", "comparative statics", "implicit function theorem", "Euler theorem"],
    keyTakeaways: [
      "Unconstrained optimization: FOC, SOC, and Hessian matrix definiteness tests",
      "Lagrangian method step-by-step with economic interpretation of the multiplier",
      "Bordered Hessian for checking sufficiency in constrained optimization",
      "Kuhn-Tucker conditions for inequality-constrained problems",
      "5+ fully worked optimization problems: utility max, cost min, profit max with constraints"
    ],
    faqs: [
      {
        q: "What is the Lagrangian multiplier and what does it represent in economics?",
        a: "The Lagrangian multiplier (lambda) represents the shadow price of the constraint - it measures the rate of change in the optimal value of the objective function per unit relaxation of the constraint. In utility maximization, lambda equals the marginal utility of income.",
      },
      {
        q: "How do I use the Bordered Hessian to check second-order conditions?",
        a: "For a maximization problem with one constraint, check the sign of the bordered Hessian determinant. For 2 variables, if the determinant of the 3x3 bordered Hessian is positive, the critical point is a constrained maximum. For minimization, it should be negative.",
      },
    ],
  },
  "du-economics-pyq-analysis-patterns-2020-2026": {
    seoTitle: "DU Economics PYQ Analysis 2020-2026: Topic Frequency, Mark Distribution & Exam Strategy",
    metaDescription: "Systematic analysis of DU B.A. (Hons) Economics previous year question papers (2020-2026). Topic-wise frequency tables, mark weightage distribution & strategic topic prioritization for semester exams.",
    focusKeyword: "DU economics previous year questions",
    secondaryKeywords: ["DU economics PYQ analysis", "DU economics exam pattern", "DU micro PYQ", "DU macro PYQ", "DU economics topic frequency"],
    lsiKeywords: ["semester exam questions", "most repeated topics", "mark distribution", "numerical vs theory", "question paper pattern", "exam strategy"],
    keyTakeaways: [
      "Topic-wise frequency tables for Micro I, II, Macro I, II, MME I, II, Stats I, II & Econometrics",
      "Most repeated topics identified (e.g., IS-LM in 6 of 7 years, Slutsky in 5 of 7 years)",
      "Mark-weightage distribution: numerical vs theory vs proof-type questions",
      "Common question patterns: prove X theorem, derive Y equation, compute Z equilibrium",
      "Strategic topic-prioritization matrix for maximum marks per hour of study"
    ],
    faqs: [
      {
        q: "Which topics are most repeated in DU Economics exams?",
        a: "Based on PYQ analysis (2020-2026), the most repeated topics include: IS-LM Model (Macro), Slutsky Equation and Consumer Choice (Micro), Lagrangian Optimization (MME), OLS Estimation (Econometrics), and Solow Growth Model (Macro II). These appear in 5-7 out of 7 year-papers.",
      },
      {
        q: "What percentage of DU Economics exam marks are numerical vs theory?",
        a: "On average across all papers, approximately 40-50% of marks come from numerical/computational questions, 30-35% from theoretical derivations and proofs, and 15-25% from essay-type analytical questions. Papers like MME and Statistics have a higher numerical share (60%+).",
      },
    ],
  },
  "how-to-write-economics-essays-research-papers-guide": {
    seoTitle: "How to Write Economics Essays & Research Papers: Structure, Citations & Academic Writing Guide",
    metaDescription: "Complete guide to writing economics essays and research papers. Learn essay structure, thesis statements, APA citations, literature review methodology, diagram integration & critical evaluation techniques.",
    focusKeyword: "how to write economics essays",
    secondaryKeywords: ["economics research paper writing", "academic writing guide economics", "APA citation economics", "economics essay structure", "literature review economics"],
    lsiKeywords: ["thesis statement", "critical analysis", "economic argument", "research methodology", "paragraph structure", "academic integrity"],
    keyTakeaways: [
      "Economics essay structure: Introduction with thesis, Body with evidence, Conclusion with policy implications",
      "How to frame economic arguments using data, theory, and empirical evidence",
      "APA 7th edition citation format with economics-specific examples",
      "Literature review methodology: finding, evaluating, and synthesizing economic research",
      "Common writing mistakes in DU economics assignments and how to avoid them"
    ],
    faqs: [
      {
        q: "How should I structure an economics essay for DU assignments?",
        a: "Follow this structure: (1) Introduction with clear thesis statement and roadmap, (2) Theoretical Framework explaining the relevant economic model, (3) Evidence section with data and empirical studies, (4) Critical Analysis evaluating limitations, (5) Conclusion with policy implications. Always integrate relevant diagrams.",
      },
      {
        q: "Which citation format should I use for economics papers at DU?",
        a: "Most DU economics departments prefer APA 7th edition format. For in-text citations use (Author, Year) format. For journal articles in the reference list: Author, A. B. (Year). Title of article. Journal Name, Volume(Issue), Pages. DOI. Always cite the original source, not secondary references.",
      },
    ],
  },
  "development-economics-theories-models-indian-policy": {
    seoTitle: "Development Economics Notes: Key Theories, Growth Models & Indian Policy Framework 2026",
    metaDescription: "Comprehensive development economics study notes covering Lewis Model, Rostow's Stages, Endogenous Growth, Poverty Measurement (Gini, HDI, MPI), India's Five Year Plans, LPG Reforms & agricultural policy.",
    focusKeyword: "development economics notes",
    secondaryKeywords: ["Lewis dual sector model", "Gini coefficient calculation", "HDI components", "India economic development", "poverty measurement economics"],
    lsiKeywords: ["big push theory", "balanced growth", "human capital", "demographic dividend", "trade liberalization", "NITI Aayog"],
    keyTakeaways: [
      "Classical theories: Lewis Dual-Sector, Rostow's Stages, Big Push, Balanced vs Unbalanced Growth",
      "Modern growth: Endogenous Growth, Romer's R&D Model, Human Capital models",
      "Poverty measurement: Gini coefficient calculation, Lorenz Curve, Headcount Ratio, Sen Index",
      "HDI and MPI composition with India's ranking and component scores",
      "India's development journey: Five Year Plans, 1991 LPG reforms, agricultural policy, demographic dividend"
    ],
    faqs: [
      {
        q: "How do you calculate the Gini coefficient?",
        a: "The Gini coefficient = A / (A + B) where A is the area between the line of perfect equality (45-degree line) and the Lorenz Curve, and (A + B) is the total area under the 45-degree line (= 0.5). A Gini of 0 means perfect equality, 1 means perfect inequality. India's Gini is approximately 0.35.",
      },
      {
        q: "What are the key components of the Human Development Index (HDI)?",
        a: "HDI has three dimensions: (1) Health - measured by life expectancy at birth, (2) Education - measured by mean years of schooling and expected years of schooling, (3) Standard of Living - measured by GNI per capita (PPP). Each dimension index is calculated using goalposts, and HDI = geometric mean of all three indices.",
      },
    ],
  },
  "public-finance-taxation-fiscal-policy-notes-india": {
    seoTitle: "Public Finance & Taxation Notes: GST, Income Tax, Fiscal Policy & Indian Budget Analysis",
    metaDescription: "Complete public finance study notes covering market failure, public goods, externalities, taxation theory, India's GST structure, income tax slabs 2026-27, Union Budget anatomy, FRBM Act & Finance Commission.",
    focusKeyword: "public finance taxation notes",
    secondaryKeywords: ["GST structure India", "income tax slabs 2026-27", "Laffer curve economics", "FRBM Act India", "Union Budget analysis"],
    lsiKeywords: ["public goods", "externalities", "Pigouvian tax", "Coase theorem", "fiscal deficit", "tax devolution", "Finance Commission"],
    keyTakeaways: [
      "Market Failure: public goods (non-rivalry, non-excludability), externalities, free rider problem",
      "Taxation theory: canons of taxation, progressive vs regressive, deadweight loss, Laffer Curve",
      "India's GST structure: CGST, SGST, IGST with slab rates and compliance overview",
      "Income Tax slabs 2026-27: old vs new regime comparison with worked examples",
      "Union Budget anatomy: Revenue vs Capital receipts/expenditure, FRBM targets, Finance Commission"
    ],
    faqs: [
      {
        q: "What are the GST slab rates in India?",
        a: "India's GST has four main slabs: 5% (essential items like packaged food, economy transport), 12% (processed food, business class tickets), 18% (most goods and services including restaurants, IT services), and 28% (luxury items, automobiles, aerated drinks). Some items like fresh food, healthcare, and education are GST-exempt (0%).",
      },
      {
        q: "What is the FRBM Act and why is it important?",
        a: "The Fiscal Responsibility and Budget Management (FRBM) Act 2003 sets targets for fiscal discipline: reducing fiscal deficit to 3% of GDP and eliminating revenue deficit. It mandates transparency through fiscal policy statements and medium-term fiscal plans presented with each Union Budget. The N.K. Singh Committee (2017) recommended a debt-to-GDP ratio target of 40% for the Centre.",
      },
    ],
  },
  "indifference-curve-budget-line-consumer-equilibrium-notes": {
    seoTitle: "Indifference Curve Analysis: Properties, Budget Line, Consumer Equilibrium & Solved Numericals",
    metaDescription: "Comprehensive notes on Indifference Curve Analysis including properties, budget line derivation, consumer equilibrium, and solved numerical problems.",
    focusKeyword: "indifference curve notes",
    secondaryKeywords: ["consumer equilibrium numerical", "budget line equation"],
    lsiKeywords: ["diminishing MRS", "convexity", "corner solutions", "Slutsky equation", "income effect", "substitution effect"],
    keyTakeaways: ["Indifference curves are downward sloping, convex to origin, never intersect.", "Budget line equation is M = Px.X + Py.Y", "Consumer equilibrium occurs when MRS = Px/Py."],
    faqs: [
      { q: "What is an indifference curve?", a: "An Indifference Curve (IC) is a locus of points representing combinations of two goods yielding the exact same utility." },
      { q: "What is the budget line equation?", a: "M = Px.X + Py.Y where M is total income and Px, Py are prices." }
    ]
  },
  "keynesian-multiplier-aggregate-demand-numerical-problems": {
    seoTitle: "Keynesian Multiplier & Aggregate Demand: Complete Notes with Solved Numericals",
    metaDescription: "Master the Keynesian Multiplier and Aggregate Demand with detailed notes, theory, and over 10 solved numerical problems for economics exams.",
    focusKeyword: "keynesian multiplier numerical",
    secondaryKeywords: ["aggregate demand notes", "government expenditure multiplier"],
    lsiKeywords: ["consumption function", "APC vs MPC", "paradox of thrift", "inflationary gap", "Keynesian Cross"],
    keyTakeaways: ["Multiplier is 1/(1-MPC)", "Tax multiplier is -b/(1-b)", "Balanced budget multiplier is 1"],
    faqs: [
      { q: "What is the formula for the investment multiplier?", a: "The investment multiplier k is equal to 1 / (1 - MPC) or 1 / MPS." },
      { q: "What is the paradox of thrift?", a: "If everyone tries to save more, aggregate demand falls, which can lead to lower total output and unchanged or lower total savings." }
    ]
  },
  "balance-of-payments-current-capital-account-india": {
    seoTitle: "Balance of Payments Explained: Current Account, Capital Account, India Data",
    metaDescription: "Understand the Balance of Payments, its components, India's historical data, and the 1991 crisis. Complete notes with solved numericals.",
    focusKeyword: "balance of payments notes",
    secondaryKeywords: ["current account deficit India", "BOP structure"],
    lsiKeywords: ["trade balance", "FDI", "BOP crisis", "twin deficit hypothesis", "Marshall-Lerner condition", "J-curve"],
    keyTakeaways: ["Current account deals with trade, services and transfers.", "Capital account deals with investments like FDI, FPI, and loans.", "BOP is always balanced due to double-entry bookkeeping."],
    faqs: [
      { q: "What is the difference between autonomous and accommodating transactions?", a: "Autonomous transactions happen for independent reasons like profit, while accommodating transactions are done by the central bank to correct BOP disequilibrium." },
      { q: "What caused the 1991 BOP crisis in India?", a: "A mix of heavy fiscal deficits, rising oil prices due to the Gulf War, and plummeting forex reserves." }
    ]
  },
  "theory-of-production-returns-to-scale-cost-curves-notes": {
    seoTitle: "Theory of Production: Short/Long Run, Returns to Scale & Cost Curves",
    metaDescription: "Detailed notes on Theory of Production, Returns to Scale, Isoquant properties, and Cost Curves with solved numericals.",
    focusKeyword: "theory of production notes",
    secondaryKeywords: ["returns to scale", "isoquant map", "cost curves economics"],
    lsiKeywords: ["law of variable proportions", "isocost line", "producer equilibrium", "Cobb-Douglas", "economies of scale"],
    keyTakeaways: ["Short run has fixed factors, long run has all variable factors.", "Producer equilibrium happens when MRTS = w/r.", "Long-run average cost curve is a U-shaped envelope curve."],
    faqs: [
      { q: "What are the three stages of the law of variable proportions?", a: "Increasing returns to a factor, diminishing returns to a factor, and negative returns to a factor." },
      { q: "What does the Cobb-Douglas production function show about returns to scale?", a: "If the exponents alpha and beta sum to 1, it's constant returns to scale. If >1, increasing; if <1, decreasing." }
    ]
  },
  "inflation-types-causes-wpi-cpi-india-notes": {
    seoTitle: "Inflation in India: Types, Causes, WPI vs CPI & RBI Inflation Targeting",
    metaDescription: "Learn about inflation types, causes, measurement (WPI vs CPI), Phillips curve, and RBI's inflation targeting in India.",
    focusKeyword: "types of inflation notes",
    secondaryKeywords: ["WPI vs CPI difference", "inflation targeting India"],
    lsiKeywords: ["demand-pull", "cost-push", "Phillips Curve", "stagflation", "Monetary Policy Committee"],
    keyTakeaways: ["WPI excludes services and is weighted to manufactured goods.", "CPI is what RBI uses for targeting inflation.", "RBI target is 4% with a 2% margin on either side."],
    faqs: [
      { q: "What is the difference between WPI and CPI?", a: "WPI tracks wholesale prices of goods only, whereas CPI tracks retail prices of goods and services." },
      { q: "What is stagflation?", a: "Stagflation is an economic situation characterized by high inflation and high unemployment simultaneously." }
    ]
  },
  "demand-supply-equilibrium-shifts-government-intervention": {
    seoTitle: "Demand & Supply Analysis: Equilibrium, Shifts, Controls",
    metaDescription: "Master market equilibrium, shifts in demand and supply, price ceilings, price floors, and tax incidence with worked numerical problems.",
    focusKeyword: "demand supply equilibrium",
    secondaryKeywords: ["price ceiling price floor economics", "shift in demand curve"],
    lsiKeywords: ["law of demand", "law of supply", "market equilibrium", "Giffen goods", "Veblen goods", "tax incidence", "subsidy"],
    keyTakeaways: [
      "Equilibrium occurs where quantity demanded equals quantity supplied.",
      "Shifts in demand and supply affect equilibrium price and quantity depending on relative magnitudes.",
      "Price ceilings cause shortages, while price floors cause surpluses (e.g., MSP).",
      "Tax incidence depends on the relative elasticities of demand and supply."
    ],
    faqs: [
      { q: "What is a binding price ceiling?", a: "A maximum price set below the equilibrium price, leading to a shortage." },
      { q: "Who bears the burden of a tax?", a: "The burden falls more heavily on the side of the market that is less elastic." }
    ]
  },
  "indian-five-year-plans-summary-first-to-twelfth": {
    seoTitle: "Indian Five Year Plans Summary: 1st to 12th Plan",
    metaDescription: "Complete summary of all 12 Indian Five Year Plans from 1951 to 2017, their objectives, models, and transition to NITI Aayog.",
    focusKeyword: "five year plans India summary",
    secondaryKeywords: ["Indian planning commission history", "NITI Aayog vs Planning Commission"],
    lsiKeywords: ["Mahalanobis model", "Harrod-Domar", "Green revolution", "economic liberalization", "NITI Aayog", "Plan Holidays"],
    keyTakeaways: [
      "The First Plan focused on agriculture, while the Second Plan heavily prioritized heavy industries.",
      "Plan holidays and annual plans were used during periods of crisis like wars and BOP issues.",
      "NITI Aayog replaced the Planning Commission in 2015, shifting from a top-down to a bottom-up approach."
    ],
    faqs: [
      { q: "Why was the Planning Commission replaced?", a: "To promote cooperative federalism and bottom-up planning rather than centralized top-down control." },
      { q: "What was the core focus of the Second Five Year Plan?", a: "Rapid industrialization, especially basic and heavy industries based on the Mahalanobis model." }
    ]
  },
  "poverty-inequality-india-measurement-tendulkar-committee": {
    seoTitle: "Poverty & Inequality in India: Tendulkar Committee & Gini",
    metaDescription: "Understand poverty measurement in India, Tendulkar and Rangarajan committees, Gini coefficient, and major anti-poverty programs.",
    focusKeyword: "poverty in India notes",
    secondaryKeywords: ["Tendulkar committee poverty line", "Gini coefficient India"],
    lsiKeywords: ["absolute poverty", "relative poverty", "Lorenz curve", "multidimensional poverty index", "headcount ratio", "MGNREGA"],
    keyTakeaways: [
      "Tendulkar committee shifted away from a strict calorie anchor to include health and education.",
      "Gini coefficient measures inequality, with 0 being perfect equality and 1 being perfect inequality.",
      "The MPI measures deprivation across health, education, and standard of living."
    ],
    faqs: [
      { q: "What is the difference between absolute and relative poverty?", a: "Absolute poverty refers to lacking basic needs, whereas relative poverty measures income inequality compared to others." },
      { q: "How is the Gini coefficient calculated?", a: "It is the ratio of the area between the Lorenz curve and the line of equality to the total area under the line of equality." }
    ]
  },
  "utility-theory-cardinal-ordinal-demand-curve-derivation": {
    seoTitle: "Utility Theory Notes: Cardinal, Ordinal & Demand Derivation",
    metaDescription: "Comprehensive notes on utility theory, covering Cardinal vs Ordinal approaches, Law of DMU, and deriving the demand curve.",
    focusKeyword: "utility theory notes economics",
    secondaryKeywords: ["law of diminishing marginal utility", "cardinal vs ordinal utility"],
    lsiKeywords: ["indifference curve", "marginal rate of substitution", "equi-marginal principle", "Hicks-Allen", "Marshallian demand"],
    keyTakeaways: [
      "Cardinal utility assumes satisfaction is measurable in utils, while Ordinal utility relies on preference ranking.",
      "The Law of Diminishing Marginal Utility states that additional satisfaction decreases with successive consumption.",
      "Consumer equilibrium in the ordinal approach occurs where the highest indifference curve is tangent to the budget line."
    ],
    faqs: [
      { q: "What is the equi-marginal principle?", a: "Consumers maximize utility by equating the ratio of marginal utility to price across all goods consumed." },
      { q: "Why are indifference curves convex to the origin?", a: "Due to the diminishing marginal rate of substitution between the two goods." }
    ]
  },
  "foreign-exchange-market-exchange-rate-determination-india": {
    seoTitle: "Foreign Exchange Market: Exchange Rates, PPP & India",
    metaDescription: "Master forex markets, PPP theory, Interest Rate Parity, and India's exchange rate policies with worked examples.",
    focusKeyword: "exchange rate determination notes",
    secondaryKeywords: ["PPP theory economics", "forex market India"],
    lsiKeywords: ["spot rate", "forward rate", "managed float", "RBI intervention", "REER", "sterilization", "Mundell-Fleming"],
    keyTakeaways: [
      "Exchange rates are determined by the demand and supply of forex in a floating system.",
      "Purchasing Power Parity (PPP) links exchange rates to relative price levels and inflation rates.",
      "India currently operates under a managed float system, with the RBI intervening to prevent excessive volatility."
    ],
    faqs: [
      { q: "What is a managed float exchange rate system?", a: "A system where the market determines the exchange rate, but the central bank intervenes occasionally to smooth out extreme fluctuations." },
      { q: "What is the difference between absolute and relative PPP?", a: "Absolute PPP relies on exact price levels (Law of One Price), while relative PPP uses inflation differentials to explain exchange rate changes over time." }
    ]
  },
  "microeconomics-sem-1-unit-1-exploring-economics-ppf-notes": {
    seoTitle: "Microeconomics Unit 1: Scarcity, PPF & Mankiw Principles",
    metaDescription: "Complete DU Semester 1 Unit 1 study notes covering scarcity, choice, opportunity cost, Production Possibility Frontier (PPF) derivations, MRT, Mankiw's 10 principles & solved numericals.",
    focusKeyword: "microeconomics sem 1 unit 1 notes",
    secondaryKeywords: [
      "DU economics PPF derivations",
      "Mankiw 10 principles of economics",
      "opportunity cost numericals",
      "marginal rate of transformation"
    ],
    lsiKeywords: [
      "scarcity",
      "marginal decision making",
      "circular flow model",
      "positive vs normative economics",
      "Delhi University",
      "increasing opportunity cost"
    ],
    keyTakeaways: [
      "Scarcity forces trade-offs; every choice has an opportunity cost.",
      "The PPF is concave because of the Law of Increasing Opportunity Cost (specialised resources).",
      "MRT equals the absolute slope of the PPF and the ratio of marginal costs (MCx/MCy).",
      "Rational individuals optimize at the margin (MB = MC), ignoring sunk costs."
    ],
    faqs: [
      {
        q: "Why is the PPF concave to the origin?",
        a: "The PPF is concave due to the Law of Increasing Opportunity Cost. Since resources are not perfectly adaptable to the production of all goods, moving resources from one good to another becomes increasingly costly, making the MRT increase."
      },
      {
        q: "What is the difference between positive and normative economics?",
        a: "Positive economics describes 'what is' based on testable facts and data. Normative economics deals with 'what ought to be' based on value judgments and opinions."
      },
      {
        q: "How do you calculate MRT from a PPF equation?",
        a: "Find the first derivative of the PPF equation (dY/dX). The MRT is the absolute value of this derivative, representing the slope."
      }
    ]
  },
  "microeconomics-sem-1-unit-5-perfect-competition-short-long-run-equilibrium": {
    seoTitle: "Introductory Microeconomics Unit 5: Perfect Competition Notes",
    metaDescription: "Master DU Sem 1 Unit 5: Perfect competition assumptions, firm vs industry equilibrium, shutdown point P < AVC, short/long-run supply curve derivation & social efficiency.",
    focusKeyword: "perfect competition notes",
    secondaryKeywords: ["DU microeconomics sem 1 unit 5", "short run equilibrium competitive firm", "long run industry supply curve", "shutdown point perfect competition"],
    lsiKeywords: ["price taker", "marginal revenue", "marginal cost", "average variable cost", "allocative efficiency", "deadweight loss"],
    keyTakeaways: [
      "Perfectly competitive firms are price takers, resulting in a horizontal demand curve where P = AR = MR.",
      "The short-run shutdown point occurs where Price equals the minimum of Average Variable Cost (P = min AVC).",
      "In long-run equilibrium, entry and exit drive economic profits to zero, resulting in P = LMC = LAC."
    ],
    faqs: [
      { q: "Why is a perfectly competitive firm a price taker?", a: "Because there are many buyers and sellers trading identical products, no single firm has the market power to influence the price." },
      { q: "When should a competitive firm shut down in the short run?", a: "When the market price falls below its minimum Average Variable Cost (min AVC), meaning it cannot even cover its day-to-day operating expenses." },
      { q: "What is the shape of the long-run industry supply curve?", a: "It can be horizontal (constant cost), upward sloping (increasing cost), or downward sloping (decreasing cost), depending on how input prices react to industry expansion." }
    ]
  },
  "microeconomics-sem-1-unit-4-production-costs-isoquants-returns-to-scale": {
    seoTitle: "Microeconomics Unit 4: Production, Isoquants & Cost Curves",
    metaDescription: "Detailed DU Sem 1 Unit 4 notes covering Law of Variable Proportions, Isoquants, Returns to Scale, and short/long run Cost Curves with solved numericals.",
    focusKeyword: "microeconomics sem 1 unit 4 notes",
    secondaryKeywords: [
      "production function DU notes",
      "law of variable proportions",
      "isoquants and MRTS",
      "returns to scale euler theorem",
      "short run and long run cost curves"
    ],
    lsiKeywords: [
      "marginal product",
      "average product",
      "expansion path",
      "isocost line",
      "LAC envelope curve",
      "Delhi University",
      "cost minimization"
    ],
    keyTakeaways: [
      "The Law of Variable Proportions creates the U-shape of short-run cost curves.",
      "Stage II is the only rational zone of production because both marginal and average products are positive but falling.",
      "Cost minimization occurs where the MRTS equals the input price ratio (w/r).",
      "The Long-Run Average Cost (LAC) curve is the envelope of short-run average cost curves, driven by returns to scale."
    ],
    faqs: [
      {
        q: "Why does the Marginal Cost (MC) curve cut the Average Total Cost (ATC) curve at its minimum?",
        a: "Mathematically, when MC is less than ATC, it pulls the average down. When MC is greater than ATC, it pulls the average up. Thus, they must intersect at the exact minimum point of ATC."
      },
      {
        q: "What is the difference between Returns to a Factor and Returns to Scale?",
        a: "Returns to a Factor (short-run) analyzes changes in output when one factor is varied while others are fixed. Returns to Scale (long-run) analyzes changes in output when all inputs are varied proportionately."
      },
      {
        q: "What is Euler's Theorem in production?",
        a: "Euler's Theorem states that if a production function is homogeneous of degree one (constant returns to scale) and each factor is paid its marginal product, the total output is exactly exhausted by the total factor payments."
      }
    ]
  },
  "microeconomics-sem-1-unit-8-factor-market-pricing-labor-supply-economic-rent": {
    seoTitle: "Factor Markets & Labor Supply: DU Microeconomics Unit 8",
    metaDescription: "Master DU Sem 1 Unit 8: Derived demand, VMPL vs MRPL, backward-bending labor supply, monopsony & economic rent. With solved PYQ numericals.",
    focusKeyword: "factor markets labor supply DU notes",
    secondaryKeywords: [
      "VMPL vs MRPL",
      "backward bending labor supply",
      "monopsony exploitation",
      "economic rent microeconomics"
    ],
    lsiKeywords: [
      "marginal product of labor",
      "derived demand",
      "Hicks-Marshall laws",
      "transfer earnings",
      "quasi-rent",
      "income and substitution effect"
    ],
    keyTakeaways: [
      "Factor demand is a derived demand: VMPL = P * MPL for competitive markets, MRPL = MR * MPL for monopolies.",
      "The labor supply curve bends backward when the income effect of a wage increase outweighs the substitution effect.",
      "In a monopsony, Marginal Factor Cost (MFC) exceeds the wage, leading to monopsonistic exploitation where workers are paid less than their VMPL.",
      "Economic rent is any payment to a factor above its transfer earnings, entirely dependent on the factor's supply elasticity."
    ],
    faqs: [
      {
        q: "What is the difference between VMPL and MRPL?",
        a: "VMPL is the value of the marginal product (P * MPL) used by competitive firms. MRPL is the marginal revenue product (MR * MPL) used by monopolists."
      },
      {
        q: "Why does the labor supply curve bend backward?",
        a: "At very high wages, workers are wealthy enough that the income effect (desire for more leisure) dominates the substitution effect (higher opportunity cost of leisure)."
      },
      {
        q: "What is monopsonistic exploitation?",
        a: "It occurs when a single buyer of labor (monopsony) pays a wage lower than the worker's marginal contribution to revenue (VMPL or MRPL)."
      }
    ]
  },
  "microeconomics-sem-1-unit-2-demand-supply-elasticity-market-equilibrium": {
    seoTitle: "Demand, Supply & Elasticity Notes: DU Micro Unit 2",
    metaDescription: "Master DU Sem 1 Unit 2: Demand & supply laws, elasticity formulas (price, income, cross), market equilibrium, price controls, tax incidence & 8+ solved numericals.",
    focusKeyword: "microeconomics unit 2 DU notes",
    secondaryKeywords: [
      "elasticity formulas microeconomics",
      "market equilibrium solved examples",
      "tax incidence and deadweight loss",
      "price ceiling and floor effects"
    ],
    lsiKeywords: [
      "cross-price elasticity",
      "Giffen goods",
      "Walrasian stability",
      "arc elasticity method",
      "consumer surplus",
      "Engel curve"
    ],
    keyTakeaways: [
      "Demand movements are caused solely by price changes; shifts by non-price factors.",
      "Elasticity measures percentage responsiveness; along a linear demand curve, elasticity falls from infinity to zero.",
      "Tax incidence depends strictly on relative elasticity, not on who legally pays the tax; the inelastic side bears the burden.",
      "Price controls like ceilings cause shortages, while floors cause surpluses; both create deadweight loss."
    ],
    faqs: [
      {
        q: "How do you calculate arc elasticity?",
        a: "Arc elasticity uses average price and average quantity instead of initial values to avoid the base-point problem. Formula: [(Q2-Q1)/avg(Q)] / [(P2-P1)/avg(P)]."
      },
      {
        q: "What is the difference between a price ceiling and a price floor?",
        a: "A price ceiling is a legal maximum (e.g., rent control) that causes a shortage. A price floor is a legal minimum (e.g., MSP) that causes a surplus."
      },
      {
        q: "How does elasticity affect tax incidence?",
        a: "The burden of a tax falls disproportionately on the side of the market that is less elastic (less responsive to price changes)."
      }
    ]
  },
  "microeconomics-sem-1-unit-6-monopoly-price-discrimination-deadweight-loss": {
    seoTitle: "Microeconomics Unit 6: Monopoly & Price Discrimination Notes",
    metaDescription: "Complete DU Sem 1 Unit 6 notes: Monopoly equilibrium, Lerner Index, Deadweight Loss & Price Discrimination. Includes 8+ solved numerical problems.",
    focusKeyword: "monopoly and price discrimination notes",
    secondaryKeywords: ["DU microeconomics sem 1 unit 6", "Lerner index numerical", "deadweight loss monopoly", "third degree price discrimination"],
    lsiKeywords: ["natural monopoly", "Herfindahl-Hirschman Index", "welfare analysis", "peak-load pricing", "marginal revenue elasticity"],
    keyTakeaways: [
      "A monopolist never operates on the inelastic portion of the demand curve because MR would be negative.",
      "The Lerner Index (P - MC)/P measures monopoly power and equals 1/|e|.",
      "First-degree price discrimination extracts all consumer surplus, resulting in zero deadweight loss.",
      "A monopoly has no unique supply curve because price is determined jointly by MC and the demand curve's elasticity."
    ],
    faqs: [
      { q: "Why is MR less than P for a monopolist?", a: "Because to sell an additional unit, the monopolist must lower the price on all prior units sold, pushing Marginal Revenue below the price." },
      { q: "What is 3rd degree price discrimination?", a: "Charging different prices in different sub-markets based on their price elasticities, using the rule MR1 = MR2 = MC." }
    ]
  },
  "microeconomics-sem-1-unit-3-consumer-theory-indifference-curves-slutsky": {
    seoTitle: "Microeconomics Unit 3: Consumer Theory & Slutsky Notes",
    metaDescription: "Complete DU Sem 1 Unit 3 notes on consumer preference axioms, utility functions (Cobb-Douglas, Leontief), budget constraint, MRS tangency, Hicksian/Slutsky decomposition & WARP.",
    focusKeyword: "microeconomics sem 1 unit 3",
    secondaryKeywords: [
      "consumer theory",
      "indifference curves",
      "slutsky equation",
      "DU economics unit 3",
      "budget line"
    ],
    lsiKeywords: [
      "marginal rate of substitution",
      "WARP",
      "substitution effect",
      "income effect",
      "Cobb-Douglas",
      "Leontief"
    ],
    keyTakeaways: [
      "Consumer theory models rational choice based on completeness, transitivity, monotonicity, and convexity axioms.",
      "Optimal consumption bundle is typically an interior solution where MRS equals the price ratio.",
      "Slutsky and Hicksian decompositions split the total price effect into substitution and income effects.",
      "WARP ensures consistency in consumer choices across different budget constraints."
    ],
    faqs: [
      {
        q: "What is the difference between Slutsky and Hicksian decompositions?",
        a: "Hicks holds utility constant, keeping the consumer on the original indifference curve. Slutsky holds purchasing power constant, allowing the consumer to buy their exact original bundle at new prices."
      },
      {
        q: "When do corner solutions occur in consumer theory?",
        a: "Corner solutions typically occur with perfect substitutes, where the consumer spends their entire budget on one good because the MRS does not equal the price ratio."
      },
      {
        q: "What does the Weak Axiom of Revealed Preference (WARP) state?",
        a: "If bundle A is chosen over an affordable bundle B in one situation, bundle B can never be chosen over bundle A when both are affordable in a different situation."
      }
    ]
  },
  "microeconomics-sem-1-unit-9-market-failure-externalities-public-goods-notes": {
    seoTitle: "Introductory Micro Unit 9: Externalities & Public Goods",
    metaDescription: "Complete DU Sem 1 Unit 9 notes: Negative/positive externalities, Pigouvian taxes, Coase theorem, pure public goods, Samuelson condition & tragedy of the commons.",
    focusKeyword: "microeconomics unit 9 externalities public goods",
    secondaryKeywords: ["Coase theorem", "Pigouvian tax calculation", "market failure economics", "Samuelson condition"],
    lsiKeywords: ["free-rider problem", "tragedy of the commons", "MSC and MPC", "adverse selection", "tradable permits"],
    keyTakeaways: [
      "Market failure occurs when competitive markets are not Pareto efficient, mainly due to externalities or public goods.",
      "The Coase Theorem shows that private bargaining can solve externalities if property rights are defined and transaction costs are zero.",
      "Pigouvian taxes correct negative externalities by internalizing the Marginal External Cost (MEC).",
      "Public goods are non-rival and non-excludable, leading to free-riding; their optimal provision requires summing demand curves vertically (Samuelson Condition)."
    ],
    faqs: [
      {
        q: "What is the difference between a private good and a public good?",
        a: "Private goods are excludable and rival in consumption. Public goods are non-excludable (cannot prevent use) and non-rival (one's use doesn't diminish another's)."
      },
      {
        q: "How do you calculate a Pigouvian tax?",
        a: "The optimal Pigouvian tax is set equal to the Marginal External Cost (MEC) evaluated precisely at the socially optimal quantity (Q*), not at the free-market quantity."
      },
      {
        q: "Why do common resources face the tragedy of the commons?",
        a: "Because they are non-excludable but rival, individuals overuse them based on Average Product rather than Marginal Product, ignoring the negative externality on others."
      }
    ]
  },
  "du-sem-1-introductory-microeconomics-complete-chapter-wise-master-notes": {
    seoTitle: "DU Semester 1 Microeconomics: Master Notes & Formula Sheet",
    metaDescription: "The ultimate DU Semester 1 Introductory Microeconomics master guide: Unit-by-unit syllabus roadmap, chapter links, formula cheat sheet, PYQ & 30-day plan.",
    focusKeyword: "DU Semester 1 Microeconomics",
    secondaryKeywords: [
      "DU introductory microeconomics syllabus",
      "microeconomics formula sheet",
      "DU sem 1 economics honours",
      "microeconomics PYQ analysis"
    ],
    lsiKeywords: [
      "UGCF 2022",
      "Slutsky equation",
      "Cournot model",
      "consumer theory",
      "price discrimination",
      "market failure"
    ],
    keyTakeaways: [
      "Introductory Microeconomics is a 4-credit Core Discipline Subject (DSC) under the UGCF 2022 framework.",
      "Unit 3 (Consumer Theory) and Units 6 & 7 (Market Structures) carry the highest weightage in the exam.",
      "The paper is heavily mathematical; derivations and perfectly labeled diagrams are essential for a top grade.",
      "Solving past 5-year PYQs under timed conditions is the most effective way to prepare for the final 90-mark theory exam."
    ],
    faqs: [
      {
        q: "What is the weightage of numericals in the DU Semester 1 Microeconomics exam?",
        a: "Typically, the exam is roughly 60% numericals and mathematical derivations, and 40% graphical theory. Practicing derivations like Slutsky and Cournot is mandatory."
      },
      {
        q: "Which books should I refer to for Sem 1 Introductory Microeconomics?",
        a: "Hal Varian for Consumer Theory and Market Structures, and Gregory Mankiw for fundamentals, elasticity, and externalities, as prescribed by the DU syllabus."
      },
      {
        q: "How many hours of study does the microeconomics paper require?",
        a: "Consistent practice of 1-2 hours daily focusing on mathematical derivations and graphs is sufficient. The 30-day intensive revision plan in this guide outlines exactly what to study."
      }
    ]
  },
  "microeconomics-sem-1-unit-7-monopolistic-competition-oligopoly-game-theory": {
    seoTitle: "Introductory Micro Unit 7: Monopolistic Competition & Oligopoly",
    metaDescription: "Comprehensive DU Sem 1 Unit 7 notes: Chamberlin monopolistic competition, Excess Capacity Theorem, Sweezy Kinked Demand, Cournot Duopoly, Bertrand Paradox & Game Theory.",
    focusKeyword: "microeconomics unit 7 monopolistic competition oligopoly",
    secondaryKeywords: ["Cournot Duopoly", "Bertrand Paradox", "Excess Capacity Theorem", "Sweezy Kinked Demand"],
    lsiKeywords: ["Game Theory", "Prisoner's Dilemma", "Nash Equilibrium", "Stackelberg Model", "Cartels"],
    keyTakeaways: [
      "Monopolistic competition features differentiated products, leading to a downward-sloping dd curve and long-run tangency equilibrium with excess capacity.",
      "The Cournot model assumes firms compete on quantity, with equilibrium found at the intersection of reaction functions.",
      "The Bertrand model assumes price competition, leading to a perfectly competitive outcome (P=MC) even with two firms.",
      "Game theory models strategic interactions, showing why cartels are unstable due to dominant strategies leading to a suboptimal Nash Equilibrium."
    ],
    faqs: [
      {
        q: "What is Chamberlin's Excess Capacity Theorem?",
        a: "It states that in the long run, monopolistically competitive firms produce below the minimum of their average cost curve due to the tangency of a downward-sloping demand curve, resulting in excess capacity."
      },
      {
        q: "How does Cournot differ from Stackelberg?",
        a: "Cournot assumes firms choose output simultaneously. Stackelberg assumes one firm acts as a leader and chooses first, giving it a first-mover advantage and higher market share."
      },
      {
        q: "What is the kinked demand curve model?",
        a: "Sweezy's model explains price rigidity in oligopoly by assuming rivals will match price cuts but ignore price increases, creating a kink in demand and a gap in marginal revenue."
      }
    ]
  }
};
